/**
 * B62/B60 — the component vocabulary every surface consumes. Rules the
 * tests pin: real form elements under the skins (never styled divs),
 * label/control association BY CONSTRUCTION, honest disabled/busy/error
 * affordances, integer-minor-unit money. Styling is ONLY t-* classes
 * from tokens.css (the sheet's drift wall enforces the pairing).
 */
import { Component, createContext, useContext, useId, useState, type CSSProperties, type ErrorInfo, type ReactNode } from "react";
import { Icon, type IconName } from "./icons.js";
import { useTransactTheme } from "./theme.js";
import { minorToDisplay, displayToMinor } from "./money.js";
import { sizedPhotoUrl } from "@transact/domain";
import type { FieldWidth } from "./forms.js";
import { codeCopyWords, copyCodeEffect, type ClipboardLike, type CodeCopyOutcome } from "./copy.js";

/* ------------------------------------------------------------ Button */

/* B403 #16 — "textlink" is the frames' caps TEXT-LINK register (38:2's CLOSE,
   43:743's row actions, 68:2's DISABLE): an action wearing quiet caps words,
   no box. One shared face so every surface's links agree (#13 (b)/#17).
   B403 #33 — "textlink-accent" is the same anatomy in the ACCENT nav-link
   register (73:2 amendment: MANAGE →'s family, Rajdhani SemiBold 11 ls3). */
export type ButtonVariant = "primary" | "neutral" | "danger" | "ghost" | "textlink" | "textlink-danger" | "textlink-accent";

export function Button(props: {
  onClick: () => void;
  children: ReactNode;
  variant?: ButtonVariant;
  /**
   * B397 leg (2) — the sheet's LARGE money step (72:2 CONTINUE: h49, r10,
   * R-SB 13 ls4). A size, not a new kind: the accent rule and every wall
   * ride the variant unchanged.
   */
  size?: "lg";
  disabled?: boolean;
  busy?: boolean;
  type?: "button" | "submit";
  /** names the action for AT when the visible label alone is ambiguous (e.g. per-row Remove) */
  ariaLabel?: string;
  /**
   * B122 — a system id parked where a bug report can find it and no
   * screen reader reads it aloud. NOT a place for the reason a control
   * is walled: B285 found `title` and a visible note stating the same
   * sentence twice on every disabled door in the fleet, and a tooltip
   * needs a hover a touch or keyboard operator may have no way to
   * perform. A walled control draws its reason and points with
   * `describedBy` — the drift wall refuses `disabled` + `title` together.
   */
  title?: string;
  /**
   * B285 — the id of the visible note that says why this control is
   * walled. One statement, drawn, and the control announces it after its
   * own name.
   */
  describedBy?: string;
  /**
   * B227 — R1's own instruction, taken: "if the primitive you need does
   * not exist, grow it in the package first". Guided setup's approve
   * control stayed a RAW button for one reason — the approval gate pins
   * the plan digest on it (`data-digest`, so a test can prove the button
   * quotes the CURRENT plan and never a stale preview), and Button had no
   * way to carry it. A stamp is not chrome: keys arrive as `data-<key>`,
   * so a caller can mark a control for a wall without hand-rolling the
   * button and losing every affordance this component owns.
   */
  data?: Readonly<Record<string, string>>;
  /**
   * B258 — R1's own instruction again ("if the primitive you need does not
   * exist, grow it in the package first"), on B227's precedent. A two-state
   * control — reveal/hide, show/collapse — is a TOGGLE, and the house
   * already renders toggles as `aria-pressed`. Before this, an app that
   * wanted one had to hand-roll a raw <button> and lose every affordance
   * this component owns; that is exactly how the channels screen shipped a
   * payments-key reveal whose state no assistive tech could read. Left
   * undefined the attribute is ABSENT — a plain button is not a toggle
   * stuck in the "off" position.
   */
  pressed?: boolean;
  /**
   * B387 — a DISCLOSURE is not a toggle. `pressed` (B258) says "this
   * control is on"; a button that reveals a region says "the region below
   * is open", which assistive tech reads as `aria-expanded` paired with
   * `aria-controls` naming the region. The picker's map door needed the
   * second pair and the package had only the first, so on B227/B258's own
   * precedent it grows here rather than being hand-rolled in an app.
   * Left undefined both attributes are ABSENT: a plain button is not a
   * disclosure stuck shut.
   */
  expanded?: boolean;
  /** The id of the region `expanded` describes; ignored without it. */
  controls?: string;
  /**
   * B403 (38:2) — a door that NAVIGATES (the product page's ADD A
   * DEPARTURE goes to Supply) renders as a real <a> wearing the same
   * face, on B227/B258/B387's precedent: the primitive grows in the
   * package rather than an app hand-rolling a link dressed as a button.
   * Ignored when the control is disabled or busy — a dead link is not a
   * walled door, and the walls (`describedBy`) ride the <button> arm.
   */
  href?: string;
}): ReactNode {
  const variant = props.variant ?? "primary";
  const busy = props.busy === true;
  const disabled = props.disabled === true || busy;
  const stamps = Object.fromEntries(Object.entries(props.data ?? {}).map(([key, value]) => [`data-${key}`, value]));
  if (props.href !== undefined && !disabled) {
    return (
      <a
        className={`t-btn t-btn--${variant}${props.size === "lg" ? " t-btn--lg" : ""}`}
        href={props.href}
        onClick={props.onClick}
        aria-label={props.ariaLabel}
        aria-describedby={props.describedBy}
        title={props.title}
        {...stamps}
      >
        {props.children}
      </a>
    );
  }
  return (
    <button
      type={props.type ?? "button"}
      className={`t-btn t-btn--${variant}${props.size === "lg" ? " t-btn--lg" : ""}`}
      onClick={props.onClick}
      disabled={disabled}
      aria-disabled={disabled ? "true" : undefined}
      aria-busy={busy ? "true" : undefined}
      aria-pressed={props.pressed === undefined ? undefined : props.pressed ? "true" : "false"}
      aria-expanded={props.expanded === undefined ? undefined : props.expanded ? "true" : "false"}
      aria-controls={props.expanded === undefined ? undefined : props.controls}
      aria-label={props.ariaLabel}
      aria-describedby={props.describedBy}
      title={props.title}
      {...stamps}
    >
      {props.children}
    </button>
  );
}

/* ------------------------------------------------------------ OneWayDoor */

/**
 * B286 — the pre-click statement, and the reason the fleet had none.
 *
 * `docs/DESIGN.md` line 55 has said since B61 that "Danger actions say
 * what is irreversible before the one-way door". The package had two
 * things that LOOK like the answer and neither is: `PlanReviewCard` is
 * an agent PLAN review — a titled card of labeled rows, right for
 * "here is what I am about to do to nine orders" and absurd on a roster
 * row's Disable; and admin's `SlotCancelEscalation` demands a TYPED
 * reason, which is correct for canceling a sailing with paid orders on
 * it and far too heavy for turning off a discount code. So a row that
 * wanted one sentence had nowhere to reach, and 12 danger doors in the
 * fleet opened on a single press. Pathik found the twelfth by pressing
 * it, and dev-ferry's FERRY10 promotion is still off (B288).
 *
 * WHY THE SENTENCE IS DRAWN ON ARMING AND NOT ALWAYS. A roster of ten
 * promotions would otherwise carry ten identical warnings for an
 * operator who came to read prices — and B285 closed one week ago on
 * exactly the shape of a reason stated more times than it is needed.
 * The statement lands where the decision is: between reaching for the
 * door and going through it.
 *
 * WHY THE FACES ARE A DISCRIMINATED UNION rather than one props bag
 * with optional fields. The shut arm has no `onConfirm` — so a shut
 * door cannot be handed the act even by mistake, and "the first press
 * does not perform it" is carried by the compiler instead of by a test
 * that would need a DOM this repo does not have. `oneWayDoorNext` says
 * the other half: reaching OPENS. See the test file's own header for
 * what that leaves unproven.
 */

export type OneWayDoorState = "shut" | "open";
export type OneWayDoorPress = "reach" | "back";

/**
 * The door's transition table, explicit like every other state machine
 * in this repo. Note what is absent: there is no "confirm" press here,
 * because confirming is not a state change — it is the caller's act,
 * and a door that recorded it would be pretending to own something it
 * does not.
 */
export function oneWayDoorNext(state: OneWayDoorState, press: OneWayDoorPress): OneWayDoorState {
  if (press === "reach") return "open";
  return "shut";
}

/**
 * B501 — what a typed confirmation is worth after a press, as a decision
 * rather than a line buried in a click handler.
 *
 * Going BACK forgets what was typed. It has to: the phrase is the lock,
 * and a lock that stayed open while the door shut would mean the NEXT
 * opening started already unlocked — one stray press from the act the
 * typing exists to prevent. Reaching keeps nothing either, for the same
 * reason from the other side.
 *
 * It lives here, beside the transition table, because the component has no
 * DOM in this repo's tests and a rule held only inside a handler is a rule
 * no test can reach — which is exactly how this one survived its first
 * mutation battery unnoticed.
 */
export function oneWayDoorTypedAfter(press: OneWayDoorPress, typed: string): string {
  void typed;
  void press;
  return "";
}

/** The drawn faces. Pure, so both are renderable without a DOM. */
/**
 * B501 — the typed confirmation, for doors where a CLICK is not enough.
 *
 * A click-to-confirm is the right wall for "retire this product". It is
 * the wrong wall for "wipe this tenant's every order, entitlement and
 * audit row" (frame 328-2): a click can be muscle memory on the wrong row,
 * and typing the thing's own name cannot. The comparison is EXACT — no
 * trim, no case-folding — because a pasted id with a stray space is a
 * paste that went wrong, and this is the wrong door to be forgiving at.
 * It is the same exactness the `demo.delete` contract asks for on the
 * wire, stated once on each side rather than negotiated between them.
 */
export interface TypedConfirmation {
  /** What must be typed, character for character — usually the thing's id. */
  readonly phrase: string;
  /** The field's real label. Never a placeholder standing in for one. */
  readonly label: string;
  /** What the operator has typed so far. */
  readonly value: string;
}

/** Exact, deliberately: see `TypedConfirmation`. */
export const typedConfirmationMet = (typed: TypedConfirmation | undefined): boolean =>
  typed === undefined || typed.value === typed.phrase;

export function OneWayDoorFace(
  props:
    | {
        readonly state: "shut";
        readonly label: string;
        readonly onReach: () => void;
        readonly ariaLabel?: string | undefined;
        /** B403 #17 — the frames draw some one-way doors as the red caps text-link (RETIRE →). */
        readonly face?: "textlink" | undefined;
        /** B549 (5) — sidecar hooks on the shut face: the door, and `textRedline` on its word. */
        readonly redline?: string | undefined;
        readonly textRedline?: string | undefined;
      }
    | {
        readonly state: "open";
        readonly label: string;
        readonly consequence: string;
        readonly onConfirm: () => void;
        readonly onBack?: (() => void) | undefined;
        readonly confirmLabel?: string | undefined;
        readonly cancelLabel?: string | undefined;
        readonly busy?: boolean | undefined;
        readonly ariaLabel?: string | undefined;
        /** B501 — hold the act shut until this is typed exactly. */
        readonly typeToConfirm?: TypedConfirmation | undefined;
        readonly onTypeToConfirm?: ((value: string) => void) | undefined;
      },
): ReactNode {
  const noteId = useId();
  if (props.state === "shut") {
    return (
      /* B286: the face is stamped (B227's `data` seam) so a SCREEN's test can prove
         its control is a one-way door and not a bare danger Button — the shut face is
         otherwise indistinguishable in static markup, which is how this class stayed
         invisible to every wall the fleet had. B403 #17: the frames draw some
         one-way doors as the red caps TEXT-LINK (43:743's RETIRE →) — `face`
         swaps the shut face's clothes; the stamp and the whole confirm flow
         stand unchanged. */
      <Button
        variant={props.face === "textlink" ? "textlink-danger" : "danger"}
        onClick={props.onReach}
        data={{ oneway: "shut", ...(props.redline !== undefined ? { redline: props.redline } : {}) }}
        {...(props.ariaLabel === undefined ? {} : { ariaLabel: props.ariaLabel })}
      >
        {props.textRedline !== undefined ? <span data-redline={props.textRedline}>{props.label}</span> : props.label}
      </Button>
    );
  }
  const busy = props.busy === true;
  const typed = props.typeToConfirm;
  const met = typedConfirmationMet(typed);
  return (
    <span className="t-oneway" role="group" aria-label={props.ariaLabel ?? props.label}>
      {/* B285's rule, honoured by construction: the sentence is DRAWN once
          and the act points at it. No `title` twin — a tooltip needs a
          hover a touch operator cannot perform. */}
      <span className="t-oneway-note" role="note" id={noteId}>
        {props.consequence}
      </span>
      {/* B501 — the field sits BETWEEN the sentence and the act, which is the
          order the operator reads in: what this costs, then what to type, then
          the button. B299's rule is honoured by the HINT: a disabled act that
          explains nothing is a wall with no cure written on it, so the cure is
          drawn at the field rather than left to be guessed from the greying.

          AND THE FIELD STAYS OPEN WHILE THE ACT IS IN FLIGHT. The first draft
          disabled it under `busy`, and B299's wall refused that — correctly:
          an act in flight is the control WORKING, not the operator being
          refused, and a face that shuts mid-save says the screen took
          something away. The door wears `busy` on the button, which is the
          one place the act actually lives. */}
      {typed === undefined ? null : (
        <Input
          id={`${noteId}-typed`}
          label={typed.label}
          value={typed.value}
          onChange={props.onTypeToConfirm ?? (() => {})}
          hint={`Type ${typed.phrase} to confirm.`}
          width="full"
        />
      )}
      <span className="t-oneway-actions">
        <Button
          variant="danger"
          onClick={props.onConfirm}
          describedBy={noteId}
          busy={busy}
          disabled={!met}
          data={{ oneway: "open" }}
        >
          {props.confirmLabel ?? props.label}
        </Button>
        {/* No way back is drawn while the act is in the air, because there
            is none to offer — a Keep-it button that cannot un-send a
            refund would be markup pretending to be a promise. */}
        {busy || props.onBack === undefined ? null : (
          <Button variant="neutral" onClick={props.onBack}>
            {props.cancelLabel ?? "Keep it"}
          </Button>
        )}
      </span>
    </span>
  );
}

/**
 * The door a screen actually reaches for: `OneWayDoorFace` with its own
 * state. `busy` holds it OPEN — an act in flight must not snap the door
 * shut under the operator who just went through it.
 */
export function OneWayDoor(props: {
  readonly label: string;
  /** What cannot be undone, in operator words. Never wire vocabulary (B31g/B32c). */
  readonly consequence: string;
  readonly onConfirm: () => void;
  readonly confirmLabel?: string | undefined;
  readonly cancelLabel?: string | undefined;
  readonly busy?: boolean | undefined;
  readonly ariaLabel?: string | undefined;
  /** B403 #17 — the shut face's clothes (the red caps text-link); flow unchanged. */
  readonly face?: "textlink" | undefined;
  /**
   * B501 — hold the act shut until this phrase is typed exactly. The door
   * owns what has been typed, the same way it owns open-or-shut: a screen
   * that had to carry this state would carry it differently each time, and
   * the point of the door is that it does not vary.
   */
  readonly typeToConfirm?: { readonly phrase: string; readonly label: string } | undefined;
  /** B549 (5) — sidecar hooks on the shut face (443:2's VOID & RELEASE SEATS). */
  readonly redline?: string | undefined;
  readonly textRedline?: string | undefined;
}): ReactNode {
  const [state, setState] = useState<OneWayDoorState>("shut");
  const [typedValue, setTypedValue] = useState("");
  if (state === "open" || props.busy === true) {
    return (
      <OneWayDoorFace
        state="open"
        label={props.label}
        consequence={props.consequence}
        onConfirm={props.onConfirm}
        onBack={() => {
          setTypedValue(oneWayDoorTypedAfter("back", typedValue));
          setState(oneWayDoorNext("open", "back"));
        }}
        confirmLabel={props.confirmLabel}
        cancelLabel={props.cancelLabel}
        busy={props.busy}
        ariaLabel={props.ariaLabel}
        {...(props.typeToConfirm === undefined
          ? {}
          : {
              typeToConfirm: { ...props.typeToConfirm, value: typedValue },
              onTypeToConfirm: setTypedValue,
            })}
      />
    );
  }
  return (
    <OneWayDoorFace
      state="shut"
      label={props.label}
      onReach={() => {
        setTypedValue(oneWayDoorTypedAfter("reach", typedValue));
        setState(oneWayDoorNext("shut", "reach"));
      }}
      ariaLabel={props.ariaLabel}
      face={props.face}
      redline={props.redline}
      textRedline={props.textRedline}
    />
  );
}

/* ------------------------------------------- SegmentedControl / Radio */

export interface ChoiceOption {
  readonly value: string;
  readonly label: string;
  readonly hint?: string;
  /** B549 — the option label's sidecar hook (the inquiry form's kind rail names each). */
  readonly redline?: string;
  /** disable ONE option without killing the group (e.g. round trip where no reverse O-D is sold) */
  readonly disabled?: boolean;
}

export function SegmentedControl(props: {
  legend: string;
  name: string;
  options: readonly ChoiceOption[];
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  /**
   * B403 #32(a) - the exemplar shows the tender group's legend ON SCREEN as a
   * Rajdhani caps head above the rail ('HOW THE MONEY COMES IN'). Default stays
   * hidden: most segmented groups sit under a visible section head already, and
   * doubling the words is its own defect. Opt in only where the redline paints one.
   */
  legendVisible?: boolean;
  /** B386 - the control's name in a redline sidecar. */
  redline?: string;
  /** B549 (441:2 `mode-active`) — the sidecar's name for the CHECKED segment's box, wherever the value sits. */
  activeRedline?: string;
}): ReactNode {
  /* B403 #36 — ONE recipe: the frames' tab anatomy (r8 track, r6 cell,
     Rajdhani-11 tracked caps) is the segmented default, and the kind="tabs"
     variant is deleted. Every frame that draws a segmented group — 76:2's
     product tabs and tender rail, 36:2's section tabs and range picker,
     the price-list dialog's form rails — draws this anatomy; none draws
     the retired B367 pill. */
  const base = "t-segmented";
  const segments = props.options.map((option) => (
    <label
      key={option.value}
      className="t-segment"
      {...(props.activeRedline !== undefined && props.value === option.value ? { "data-redline": props.activeRedline } : {})}
    >
      <input
        type="radio"
        name={props.name}
        value={option.value}
        checked={props.value === option.value}
        disabled={option.disabled === true}
        onChange={() => props.onChange(option.value)}
      />
      <span className="t-segment-label" {...(option.redline !== undefined ? { "data-redline": option.redline } : {})}>{option.label}</span>
    </label>
  ));
  const redline = props.redline === undefined ? {} : { "data-redline": props.redline };
  if (props.legendVisible === true) {
    return (
      <fieldset
        className={base + " t-segmented--labelled"}
        disabled={props.disabled === true}
        {...redline}
      >
        <legend className="t-segmented-legend">{props.legend}</legend>
        <span className="t-segmented-rail">{segments}</span>
      </fieldset>
    );
  }
  return (
    <fieldset className={base} disabled={props.disabled === true} {...redline}>
      <legend className="t-visually-hidden">{props.legend}</legend>
      {segments}
    </fieldset>
  );
}

export function RadioGroup(props: {
  legend: string;
  name: string;
  options: readonly ChoiceOption[];
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
}): ReactNode {
  return (
    <fieldset className="t-radiogroup" disabled={props.disabled === true}>
      <legend>{props.legend}</legend>
      {props.options.map((option) => (
        <label key={option.value} className="t-radio">
          <input
            type="radio"
            name={props.name}
            value={option.value}
            checked={props.value === option.value}
            onChange={() => props.onChange(option.value)}
          />
          <span>
            <span className="t-radio-label">{option.label}</span>
            {option.hint === undefined ? null : <span className="t-radio-hint"> {option.hint}</span>}
          </span>
        </label>
      ))}
    </fieldset>
  );
}

/* ------------------------------------------------------------- fields */

function describedBy(id: string, hint: string | undefined, error: string | undefined): string | undefined {
  const ids = [error === undefined ? undefined : `${id}-error`, hint === undefined ? undefined : `${id}-hint`].filter(
    (x): x is string => x !== undefined,
  );
  return ids.length === 0 ? undefined : ids.join(" ");
}

function FieldShell(props: {
  id: string;
  label: string;
  hint?: string | undefined;
  error?: string | undefined;
  /** FORMS.md R2 — sized by content class, never by what balances the row. */
  width?: FieldWidth | undefined;
  labelHidden?: boolean | undefined;
  /** B545 — a sidecar hook on the label. */
  labelRedline?: string | undefined;
  children: ReactNode;
}): ReactNode {
  return (
    <div className={props.width === undefined ? "t-field" : `t-field t-field--${props.width}`}>
      <label
        className={props.labelHidden === true ? "t-field-label t-visually-hidden" : "t-field-label"}
        htmlFor={props.id}
        {...(props.labelRedline !== undefined ? { "data-redline": props.labelRedline } : {})}
      >
        {props.label}
      </label>
      {props.children}
      {props.hint === undefined ? null : (
        <span className="t-field-hint" id={`${props.id}-hint`}>
          {props.hint}
        </span>
      )}
      {props.error === undefined ? null : (
        <span className="t-field-error" id={`${props.id}-error`}>
          {props.error}
        </span>
      )}
    </div>
  );
}

/**
 * B295 — the walled door, promoted OUT of `apps/admin/src/ui/shell.tsx`.
 *
 * B149's rule and B285's shape, in the package where every surface can
 * reach them: a shut control keeps its OWN name, the reason is DRAWN once
 * beside it in operator words, and the control points at it with
 * `aria-describedby` so a screen reader announces the name and then the
 * why. No `title` — B285 counted that tooltip saying the same sentence
 * twice on every disabled door in the fleet, and a hover is not something
 * a touch or keyboard operator can perform.
 *
 * It lived in one app for three months, which is exactly the extinction
 * the UI law warns about: the POS reached `GatedAction` across an app
 * boundary, the validator hand-rolled the door and got an unstyled note
 * (it mounts none of admin's CSS), and the till's drawer doors said
 * nothing at all.
 */
/** A shut door's press cannot happen; naming it beats an inline no-op. */
const noopAct = (): void => undefined;

export function WalledDoor(props: { readonly label: string; readonly why: string }): ReactNode {
  const noteId = useId();
  return (
    <>
      <Button variant="neutral" disabled describedBy={noteId} onClick={noopAct}>
        {props.label}
      </Button>
      <span className="t-gate-note" role="note" id={noteId}>
        {props.why}
      </span>
    </>
  );
}

/**
 * B403 #8-r — the §10 ruling (design measure 2026-08-26, Pathik can
 * overrule): an action button wears its OWN tone at rest and refuses IN
 * WORDS on press — no disabled-grey, no at-rest amber. `WalledDoor`
 * above stays for walls the operator cannot cure by typing (role
 * fences); this is the DRAFT-validation gate. B149/B285's shape holds:
 * the button keeps its own name, the why is drawn once when asked for,
 * pointed at with aria-describedby, announced politely on arrival.
 *
 * `defaultAsked` seeds the asked state so Tier-2 static renders can
 * assert the refusing face; live surfaces never pass it.
 */
export function RefusingAction(props: {
  readonly label: string;
  readonly why: string;
  readonly onAct?: () => void;
  readonly variant?: ButtonVariant;
  readonly defaultAsked?: boolean;
}): ReactNode {
  const noteId = useId();
  const [asked, setAsked] = useState(props.defaultAsked === true);
  return (
    <>
      <Button
        variant={props.variant ?? "primary"}
        {...(asked ? { describedBy: noteId } : {})}
        onClick={() => {
          setAsked(true);
        }}
      >
        {props.label}
      </Button>
      {asked && (
        <span className="t-gate-note" role="note" aria-live="polite" id={noteId}>
          {props.why}
        </span>
      )}
    </>
  );
}

/**
 * B403 #15 — the branding pair as 43:128 draws it: a painted SWATCH, the
 * role's name in the Light-14 register, and the hex beneath as a QUIET
 * editable field (the frame draws no boxed input — the hex line IS the
 * field). Blank or not-yet-hex shows the dashed absent chip, the same
 * honest face as the record chip's dash; the swatch paints only a real
 * value. The name labels the input by construction.
 */
export function SwatchField(props: {
  readonly id: string;
  readonly label: string;
  readonly value: string;
  readonly onChange: (value: string) => void;
  readonly placeholder?: string;
  /** B581 — a refused color, said AT its swatch (FieldShell's error slot, same class and id shape). */
  readonly error?: string | undefined;
}): ReactNode {
  const valid = /^#[0-9a-fA-F]{6}$/.test(props.value);
  return (
    <div className="t-swatchfield">
      {valid ? (
        <span className="t-swatchfield-chip" style={{ background: props.value }} aria-hidden="true" />
      ) : (
        <span className="t-swatchfield-chip t-swatchfield-chip--absent" aria-hidden="true" />
      )}
      <span className="t-swatchfield-main">
        <label className="t-swatchfield-name" htmlFor={props.id}>
          {props.label}
        </label>
        <input
          id={props.id}
          type="text"
          inputMode="text"
          spellCheck={false}
          placeholder={props.placeholder ?? "Not set"}
          value={props.value}
          onChange={(e) => props.onChange(e.currentTarget.value)}
          {...(props.error !== undefined ? { "aria-invalid": true, "aria-describedby": `${props.id}-error` } : {})}
        />
        {props.error === undefined ? null : (
          <span className="t-field-error" id={`${props.id}-error`}>
            {props.error}
          </span>
        )}
      </span>
    </div>
  );
}

export function Input(props: {
  id: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
  /*
   * B295 — `| undefined` to match the FieldShell these two are handed
   * straight to. A field whose hint and error come from ONE derivation
   * (the till's money entry: a refusal replaces the teaching sentence)
   * passes exactly one of them, and under exactOptionalPropertyTypes the
   * caller could not say so without spreading the props apart — which is
   * how a hand-composed second opinion gets in.
   */
  hint?: string | undefined;
  error?: string | undefined;
  type?: "text" | "email" | "search" | "tel" | "url" | "password" | "date" | "time" | "datetime-local" | "number";
  placeholder?: string;
  disabled?: boolean;
  /** B62: operator money/number entry wants the decimal keypad on touch. */
  inputMode?: "decimal" | "numeric";
  /** B195(a): fields that trigger a passive check when the operator moves on. */
  onBlur?: (() => void) | undefined;
  width?: FieldWidth;
  /**
   * B313 — R1's own instruction taken for the third time (B227's `data`,
   * B258's `pressed`, now this): if the primitive you need does not exist,
   * grow it in the package rather than hand-rolling around it.
   *
   * The guided-setup chat's answer box was a raw `<input aria-label="your
   * answer">` behind a `drift-ok` escape whose recorded reason was exactly
   * this absence — every field the shell drew wore a VISIBLE label, and the
   * box's label is deliberately not visible because the question it answers
   * is the line of transcript directly above it. Naming it again would be
   * B285's double statement in a different costume. So the screen kept a raw
   * control, and with it no `error` seam — which is how a press could be
   * dropped with nothing said (B313) with no primitive to say it on.
   *
   * The label still EXISTS, and as a real `<label htmlFor>` rather than an
   * `aria-label`: it is announced, it is a click target for the control, and
   * a translation pipeline can see it. Only the sighted reading is dropped.
   *
   * Deliberately NOT the general escape hatch it looks like. `labelHidden`
   * is walled shrink-only in `app-drift` (`labelHiddenHits`) precisely
   * because "the surrounding copy already says it" is an argument available
   * for every field on every screen, and FORMS.md's whole subject is
   * screens that were argued into making sense rather than made to.
   */
  labelHidden?: boolean | undefined;
  /**
   * B355 slice 3 — a control that belongs ON the box's own line: the till's
   * promo row is "input 315x38 + `APPLY` outline button 69x38" in the
   * frame, one line, and so is every apply/search/verify pair after it.
   *
   * B313's instruction, taken for the fourth time. Laying the button beside
   * the FIELD instead is the shape that does not work and looks like it
   * should: a field is a grid of label / box / hint, so a flex row aligns
   * the button to whichever of those happens to be last, and the alignment
   * silently changes the first time a hint or an error appears. Putting the
   * control inside the field's box row is the only placement that holds in
   * all four states, which is exactly why it belongs in the package rather
   * than in whichever app noticed first.
   *
   * The label still names the FIELD, not the pair, and the control keeps
   * its own accessible name — a trailing button is an extra affordance on
   * the box, never a second unlabelled control (FORMS.md R7).
   */
  trailing?: ReactNode;
  /**
   * B403 (61:2) — the frame's money box writes its `$` INSIDE the box, and
   * a prefixed box right-aligns its figure (§4 amounts). A display glyph,
   * never a second control; the label still names the field.
   */
  prefix?: string;
  /** B545 — a sidecar hook: the box carries it, the label carries `<hook>-label`. */
  redline?: string | undefined;
}): ReactNode {
  const box = (
    <input
      className="t-input"
      id={props.id}
      type={props.type ?? "text"}
      inputMode={props.inputMode}
      value={props.value}
      onChange={(e) => props.onChange(e.currentTarget.value)}
      onBlur={props.onBlur}
      placeholder={props.placeholder}
      disabled={props.disabled === true}
      aria-invalid={props.error === undefined ? undefined : "true"}
      aria-describedby={describedBy(props.id, props.hint, props.error)}
      {...(props.redline !== undefined ? { "data-redline": props.redline } : {})}
    />
  );
  const affixed =
    props.prefix === undefined ? (
      box
    ) : (
      <span className="t-input-affix">
        <span className="t-input-prefix" aria-hidden="true">
          {props.prefix}
        </span>
        {box}
      </span>
    );
  return (
    <FieldShell
      id={props.id}
      label={props.label}
      hint={props.hint}
      error={props.error}
      width={props.width}
      labelHidden={props.labelHidden}
      {...(props.redline !== undefined ? { labelRedline: `${props.redline}-label` } : {})}
    >
      {props.trailing === undefined ? (
        affixed
      ) : (
        <div className="t-input-row">
          {affixed}
          {props.trailing}
        </div>
      )}
    </FieldShell>
  );
}

/** B116(d): multi-line entry (origin lists, embed snippets) — a real textarea in the field shell. */
export function Textarea(props: {
  id: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
  rows?: number;
  hint?: string;
  error?: string;
  placeholder?: string;
  disabled?: boolean;
  readOnly?: boolean;
  width?: FieldWidth;
}): ReactNode {
  return (
    <FieldShell id={props.id} label={props.label} hint={props.hint} error={props.error} width={props.width}>
      <textarea
        className="t-input t-textarea"
        id={props.id}
        rows={props.rows ?? 3}
        value={props.value}
        onChange={(e) => props.onChange(e.currentTarget.value)}
        placeholder={props.placeholder}
        disabled={props.disabled === true}
        readOnly={props.readOnly === true}
        aria-invalid={props.error === undefined ? undefined : "true"}
        aria-describedby={describedBy(props.id, props.hint, props.error)}
      />
    </FieldShell>
  );
}

export function Select(props: {
  id: string;
  label: string;
  options: readonly { readonly value: string; readonly label: string }[];
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  hint?: string;
  error?: string | undefined;
  disabled?: boolean;
  width?: FieldWidth;
  /** B313's seam, same budgeted wall as Input's — the label stays real for AT. */
  labelHidden?: boolean | undefined;
  /** B549 — a sidecar hook: the box carries it, the label `<hook>-label` (Input's seam). */
  redline?: string | undefined;
}): ReactNode {
  return (
    <FieldShell
      id={props.id}
      label={props.label}
      hint={props.hint}
      error={props.error}
      width={props.width}
      labelHidden={props.labelHidden}
      {...(props.redline !== undefined ? { labelRedline: `${props.redline}-label` } : {})}
    >
      <select
        className="t-select"
        id={props.id}
        value={props.value}
        {...(props.redline !== undefined ? { "data-redline": props.redline } : {})}
        onChange={(e) => props.onChange(e.currentTarget.value)}
        disabled={props.disabled === true}
        aria-invalid={props.error === undefined ? undefined : "true"}
        aria-describedby={describedBy(props.id, props.hint, props.error)}
      >
        {props.placeholder === undefined ? null : (
          <option value="" disabled>
            {props.placeholder}
          </option>
        )}
        {props.options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    </FieldShell>
  );
}

export function MoneyInput(props: {
  id: string;
  label: string;
  currency: string;
  /** B651 — undefined = no amount yet: the field stands empty (never "0.00" standing in for "unknown"). */
  minor: number | undefined;
  onMinorChange: (minor: number | undefined) => void;
  decimals?: number;
  hint?: string;
  error?: string;
  disabled?: boolean;
  width?: FieldWidth;
}): ReactNode {
  const decimals = props.decimals ?? 2;
  return (
    <FieldShell id={props.id} label={props.label} hint={props.hint} error={props.error} width={props.width}>
      <span className="t-money">
        <span className="t-money-currency" aria-hidden="true">
          {props.currency}
        </span>
        <input
          className="t-input"
          id={props.id}
          inputMode="decimal"
          value={props.minor === undefined ? "" : minorToDisplay(props.minor, decimals)}
          onChange={(e) => props.onMinorChange(displayToMinor(e.currentTarget.value, decimals))}
          disabled={props.disabled === true}
          aria-invalid={props.error === undefined ? undefined : "true"}
          aria-describedby={describedBy(props.id, props.hint, props.error)}
        />
      </span>
    </FieldShell>
  );
}

/* ------------------------------------------------------------ Stepper */

export function Stepper(props: {
  label: string;
  value: number;
  min: number;
  max: number;
  onChange: (value: number) => void;
  disabled?: boolean;
  /** B549 — sidecar hooks: the control box, and `valueRedline` on the count. */
  redline?: string;
  valueRedline?: string;
  /** B62: buyer surfaces rename the actions ("Fewer Adult"/"More Adult") — walls unchanged. */
  decreaseLabel?: string;
  increaseLabel?: string;
  /**
   * B391 (RULING Pathik, 2026-08-19) — on a CART line the low mark is a
   * different act, not a disabled decrement: `-` at quantity 1 is
   * `cart.remove`. Given this callback the minus stays REACHABLE at `min`
   * and performs it; without one the button is walled exactly as before, so
   * every existing caller keeps the contract it was written against.
   *
   * `minDecreaseLabel` is not decoration: at the floor the button no longer
   * decreases anything, and an accessible name still saying "Decrease" would
   * describe an act the control does not perform (FORMS.md R7).
   */
  onMinDecrease?: () => void;
  minDecreaseLabel?: string;
  /**
   * B397 review #1 (2d) — the 390 sheet's stepper paints the frame's
   * 100×37 (cells 32/36/32) with the 44 hit carried by the reach seam
   * (B393). The default keeps B367's field box for the admin and the till.
   */
  frame?: "field" | "sheet" | "widget";
}): ReactNode {
  const disabled = props.disabled === true;
  const atFloor = props.value <= props.min;
  const lowMark = atFloor && props.onMinDecrease !== undefined ? props.onMinDecrease : undefined;
  return (
    <span
      className={
        props.frame === "sheet"
          ? "t-stepper t-stepper--sheet"
          : props.frame === "widget"
            ? /* B355 (checkout widget, 58:238): 104×33 (cells 31/42/31) —
                 DENSER than the sheet; the 44 hit rides the same seam. */
              "t-stepper t-stepper--sheet t-stepper--widget"
            : "t-stepper"
      }
      role="group"
      aria-label={props.label}
      {...(props.redline !== undefined ? { "data-redline": props.redline } : {})}
    >
      <button
        type="button"
        className="t-stepper-btn"
        aria-label={lowMark === undefined ? (props.decreaseLabel ?? `Decrease ${props.label}`) : (props.minDecreaseLabel ?? `Remove ${props.label}`)}
        disabled={disabled || (atFloor && lowMark === undefined)}
        onClick={lowMark === undefined ? () => props.onChange(Math.max(props.min, props.value - 1)) : lowMark}
      >
        −
      </button>
      <span className="t-stepper-value" aria-live="polite" {...(props.valueRedline !== undefined ? { "data-redline": props.valueRedline } : {})}>
        {props.value}
      </span>
      <button
        type="button"
        className="t-stepper-btn"
        aria-label={props.increaseLabel ?? `Increase ${props.label}`}
        disabled={disabled || props.value >= props.max}
        onClick={() => props.onChange(Math.min(props.max, props.value + 1))}
      >
        +
      </button>
    </span>
  );
}

/* -------------------------------------------------- structure + status */

/**
 * B368 — the frame's PAGE TITLE: the page's name in SS3 Regular 26 with
 * ONE faint teaching sentence under it, OUTSIDE the cards — never a bold
 * heading inside one. One per screen; it is the screen's h1. B403 #21:
 * one register on every page (the measured frames all draw 400/26); the
 * old weight prop and Light default are retired.
 */
export function PageTitle(props: {
  readonly title: string;
  readonly description?: ReactNode;
  /**
   * B403 (36:2) — the control that belongs ON the title's own line, at the
   * right (the money range picker). A separate row above the content would
   * drift the title's baseline the moment either side wraps; the frame
   * draws them as one row, space between.
   */
  readonly action?: ReactNode;
  /** B386 — the title block's name in a redline sidecar. */
  readonly redline?: string;
  /**
   * B564 (328:2) — a chip riding the title's own line DIRECTLY after the
   * words (the frame's DEMO chip): a fact about the thing named, so it sits
   * with the name, not with the doors at the far right.
   */
  readonly mark?: ReactNode;
}): ReactNode {
  const h1 = <h1 className="t-pagetitle-word">{props.title}</h1>;
  const word =
    props.mark === undefined ? (
      h1
    ) : (
      <span className="t-pagetitle-lead">
        {h1}
        <span className="t-pagetitle-mark">{props.mark}</span>
      </span>
    );
  return (
    <div className="t-pagetitle" {...(props.redline !== undefined ? { "data-redline": props.redline } : {})}>
      {props.action === undefined ? (
        word
      ) : (
        <div className="t-pagetitle-row">
          {word}
          {props.action}
        </div>
      )}
      {props.description !== undefined && <p className="t-pagetitle-desc">{props.description}</p>}
    </div>
  );
}


/**
 * B243 — the pressable tile: a whole-card press target whose ACCESSIBLE
 * NAME is the title alone. The title is a visible heading; the real
 * <button> is EMPTY and takes its name from that heading via
 * aria-labelledby — so what a screen reader hears is exactly what the
 * eye sees, the body content is never swallowed into the caption, and
 * the B172 caption nets (which govern fleet-authored button faces) never
 * meet a tenant's own product naming, because the button's face carries
 * no copy at all. The sheet's stretched ::after makes the WHOLE tile the
 * tap target. Body content is display information by construction —
 * interactive children would sit under the stretch, so a tile needing
 * inner actions is a Card, not a Tile (B31h: one pick per card).
 */
export function Tile(props: {
  readonly title: string;
  readonly onPress: () => void;
  readonly children?: ReactNode;
  /**
   * B355 leg (iv-a) — a tile that cannot be pressed right now, still on
   * screen. B296's instruction taken again ("grow the primitive"): the POS
   * grid needs a sold-out/not-yet-sellable face and Banner's own comment is
   * the precedent for adding the prop rather than forking the component.
   *
   * A shut tile is NOT hidden. PickerTile's doc already states the rule for
   * its own species and it is the same rule here: a fare you cannot sell at
   * this moment is INFORMATION — the seller needs to know it exists and why
   * it is unavailable, and a grid that removed it would change size under
   * the operator's hand mid-queue (FORMS.md R12's kin).
   */
  readonly disabled?: boolean;
  /**
   * Points the shut tile at the sentence that says why — said ONCE for a
   * whole group of tiles that share a reason (B285), never repeated per
   * tile. Without this the refusal and the control it explains are two
   * unrelated things to anything that cannot see the layout.
   */
  readonly describedBy?: string;
}): ReactNode {
  const titleId = useId();
  const shut = props.disabled === true;
  return (
    <div className={shut ? "t-tile t-tile--shut" : "t-tile"}>
      <h3 className="t-tile-title" id={titleId}>
        {props.title}
      </h3>
      <button
        type="button"
        className="t-tile-press"
        aria-labelledby={titleId}
        disabled={shut}
        {...(props.describedBy !== undefined ? { "aria-describedby": props.describedBy } : {})}
        onClick={props.onPress}
      />
      {props.children == null ? null : <div className="t-tile-body">{props.children}</div>}
    </div>
  );
}

/**
 * B355 leg (iv-a) — the tile GRID: an offering laid out in equal columns
 * that wrap, as `pos-sale.png` draws the till and as the pages README owes
 * the storefront's hosted booking page ("product grid, one accent action").
 *
 * Why a component and not a class an app spells — `PickerRail`'s argument
 * two sections up, unchanged: `RowList` exists so no app writes
 * `class="t-rows"` by hand, and a grid spelled in `apps/pos` would be
 * invisible to the booking page, which is the next consumer and draws the
 * same thing for the same reason.
 *
 * Why it is not `PickerRail` with a different sheet: the rail flows pills
 * along a line and lets them keep their own widths; a grid gives every
 * member an EQUAL column, which is what makes a price list scannable and
 * what §9.2's row discipline (cards sharing a row share a height) is about.
 * A caller passing `variant="grid"` to something named Rail is a name that
 * has stopped meaning anything.
 *
 * It WRAPS rather than scrolls, for the reason the rail does: an option a
 * seller cannot see is an option they do not sell.
 */
export function TileGrid(props: {
  /** names the grid for AT where no visible heading already does */
  readonly label?: string;
  /** points at the visible heading that already names it (B285's aria pairing) */
  readonly describedBy?: string;
  readonly children: ReactNode;
}): ReactNode {
  return (
    <ul
      className="t-tile-grid"
      {...(props.label !== undefined ? { "aria-label": props.label } : {})}
      {...(props.describedBy !== undefined ? { "aria-describedby": props.describedBy } : {})}
    >
      {props.children}
    </ul>
  );
}

export type Tone = "neutral" | "accent" | "success" | "warn" | "danger";

/**
 * B355/§5 — the chip: the 12% wash pill (B367's skin), pressed = §4's
 * selected state. The period switchers (D/W/M/3M/6M/Y) are rows of these;
 * `compact` is the in-card size (the 44px hit rides the reach overlay).
 */
export function Chip(props: {
  readonly label: string;
  readonly pressed: boolean;
  readonly onPress: () => void;
  readonly compact?: boolean;
  /**
   * B543 — a chip that is an ADDRESS renders as a real link: middle-click
   * and copy-link work, `aria-current` replaces `aria-pressed`, and a
   * plain click still runs the app's own navigation (default prevented) so
   * the SPA does not full-reload. A modified click (new tab) is the
   * browser's.
   */
  readonly href?: string;
}): ReactNode {
  const className = props.compact === true ? "t-chip t-chip--period" : "t-chip";
  if (props.href !== undefined) {
    return (
      <a
        className={className}
        href={props.href}
        aria-current={props.pressed ? "page" : undefined}
        onClick={(e) => {
          if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
          e.preventDefault();
          props.onPress();
        }}
      >
        {props.label}
      </a>
    );
  }
  return (
    <button type="button" className={className} aria-pressed={props.pressed} onClick={props.onPress}>
      {props.label}
    </button>
  );
}

/**
 * B549 (438:2) — a lifecycle STAGE as a tinted chip: 20px, radius 4,
 * Rajdhani SemiBold 10 tracked, the text and the 14% tint sharing ONE
 * color — never a chip with a white label (the frame's own note). Badge
 * stays the dot-and-word status; a StageChip is a compact table fact.
 */
/**
 * The four desk stages (438:2), and — B549 (5), 443:2 — the booking's
 * document facts: `paid` (green: paid, accepted), `due` (amber: open, due,
 * the detail frame's TENTATIVE), `info` (accent: issued, the party's kind)
 * and `void` (muted, struck: a voided booking or document).
 */
export type StageTone = "new" | "quoted" | "tentative" | "confirmed" | "paid" | "due" | "info" | "void";

export function StageChip(props: {
  readonly tone: StageTone;
  readonly children: ReactNode;
  /** B386 — sidecar hooks: the chip, and `textRedline` on the word. */
  readonly redline?: string;
  readonly textRedline?: string;
}): ReactNode {
  return (
    <span className={`t-stagechip t-stagechip--${props.tone}`} {...(props.redline !== undefined ? { "data-redline": props.redline } : {})}>
      <span className="t-stagechip-text" {...(props.textRedline !== undefined ? { "data-redline": props.textRedline } : {})}>
        {props.children}
      </span>
    </span>
  );
}

export function Badge(props: { tone: Tone; children: ReactNode }): ReactNode {
  // B367 — the frame's status is an 8px dot + the word, never a filled pill
  // (23:364); the tone lives on the dot, the word stays readable text.
  return (
    <span className={`t-badge t-badge--${props.tone}`}>
      <span className="t-badge-dot" aria-hidden="true" />
      {props.children}
    </span>
  );
}

export function Banner(props: {
  tone: "info" | "success" | "warn" | "danger";
  children: ReactNode;
  /**
   * B296 — R1's instruction taken again ("grow the primitive"). A banner is
   * very often the sentence that says why the door below it is shut, and
   * before this there was no way for the door to POINT at it: the refusal and
   * the control it explains were two unrelated things to anything that cannot
   * see the layout. Naming the banner is what lets `describedBy` reach it,
   * and it keeps the sentence said exactly once (B285).
   */
  id?: string;
  /**
   * B551 leg (b) — the request id the API answered with. Rendered verbatim as
   * a ReferenceTag at the banner's end, so a customer's screenshot joins a
   * log line. Absent or empty draws nothing.
   */
  reference?: string | undefined;
}): ReactNode {
  return (
    <div
      className={`t-banner t-banner--${props.tone}`}
      id={props.id}
      role={props.tone === "danger" ? "alert" : "status"}
    >
      {props.children}
      {props.reference !== undefined && props.reference !== "" ? <ReferenceTag id={props.reference} /> : null}
    </div>
  );
}

/**
 * B551 leg (b) — reads the reference id off anything an app caught: every
 * app's API error carries `requestId` (from the failure body, or the exposed
 * `apigw-requestid` header). Anything else answers undefined.
 */
export function referenceOf(err: unknown): string | undefined {
  if (err === null || typeof err !== "object") return undefined;
  const id = (err as { requestId?: unknown }).requestId;
  return typeof id === "string" && id !== "" ? id : undefined;
}

/**
 * B551 leg (b) — the reference id's ONE face: the caps "Ref" label and the id
 * as a code chip with the shared copy door (B306's outcomes, B347's 44px
 * target). The id is never reworded, truncated or styled away — it is the
 * only thing on an error screen support can search for.
 */
/**
 * B552 leg (c2) — the face a crashed screen shows instead of a blank page:
 * a caps caption, ONE plain sentence, the reference the report came back
 * with (support can search for it) and RELOAD as the one door. Never the
 * error's own text — a stack is for the report, not for the person.
 */
export function ErrorFace(props: { readonly reference?: string | undefined; readonly onReload: () => void }): ReactNode {
  return (
    <div className="t-errorface">
      <p className="t-errorface-caption">Something went wrong</p>
      {/* The refusal rides the package Banner (B213): the sentence, RELOAD
          as its way back, and the report's Ref drawn at its end. */}
      <Banner tone="danger" reference={props.reference}>
        <span className="t-errorface-sentence">This screen stopped working. Reload to carry on — anything already saved is safe.</span>{" "}
        <Button onClick={props.onReload}>Reload</Button>
      </Banner>
    </div>
  );
}

/**
 * B552 leg (c2) — a render crash inside is caught, reported ONCE through
 * `onError` (which may answer the report's reference, async), and drawn as
 * `ErrorFace`. A reporter that fails leaves the face without a reference;
 * the face never waits on the wire.
 */
export class ErrorBoundary extends Component<
  {
    readonly children: ReactNode;
    readonly onError?: (error: unknown, componentStack: string | undefined) => Promise<string | undefined> | string | undefined | void;
    readonly onReload?: () => void;
  },
  { readonly failed: boolean; readonly reference?: string | undefined }
> {
  override state: { readonly failed: boolean; readonly reference?: string | undefined } = { failed: false };

  static getDerivedStateFromError(): { failed: boolean } {
    return { failed: true };
  }

  override componentDidCatch(error: unknown, info: ErrorInfo): void {
    let answer: Promise<string | undefined> | string | undefined | void;
    try {
      answer = this.props.onError?.(error, info.componentStack ?? undefined);
    } catch {
      return;
    }
    void Promise.resolve(answer)
      .then((reference) => {
        if (typeof reference === "string" && reference.length > 0) this.setState({ reference });
      })
      .catch(() => undefined);
  }

  override render(): ReactNode {
    if (!this.state.failed) return this.props.children;
    return (
      <ErrorFace
        reference={this.state.reference}
        onReload={this.props.onReload ?? (() => (typeof window === "undefined" ? undefined : window.location.reload()))}
      />
    );
  }
}

export function ReferenceTag(props: { id: string; clipboard?: ClipboardLike }): ReactNode {
  const [outcome, setOutcome] = useState<CodeCopyOutcome>("untried");
  const copied = outcome === "copied";
  return (
    <span className="t-ref" data-reference={props.id}>
      <span className="t-ref-label">Ref</span>
      <span className="t-code">
        <code className="t-code-text">{props.id}</code>
        <button
          type="button"
          className="t-code-copy"
          aria-label={`Copy reference ${props.id}`}
          onClick={() => {
            const clip =
              props.clipboard ?? (typeof navigator === "undefined" ? undefined : (navigator.clipboard as ClipboardLike | undefined));
            void copyCodeEffect({ code: props.id, clipboard: clip }).then((next) => {
              setOutcome(next);
              if (next === "copied") setTimeout(() => setOutcome("untried"), 1600);
            });
          }}
        >
          {copied ? "Copied" : "Copy"}
        </button>
      </span>
      {outcome === "refused" || outcome === "unavailable" ? (
        <span className="t-code-note" role="status">
          {codeCopyWords(outcome)}
        </span>
      ) : null}
    </span>
  );
}

export function Toast(props: { children: ReactNode; inline?: boolean }): ReactNode {
  // B83: inline places the toast in normal flow (gallery states matrix,
  // docs) — same surface/pair tokens, no fixed overlay.
  return (
    <div className={props.inline === true ? "t-toast t-toast--inline" : "t-toast"} role="status">
      {props.children}
    </div>
  );
}

/**
 * B281 — the persistent cart while browsing (B31h), package-owned. Zero
 * renders nothing: an empty cart is not news. `inline` places it in
 * normal flow (gallery, docs — the Toast precedent); the default face is
 * fixed-position chrome the storefront floats over the browse screens.
 */
export function CartBadge(props: { readonly count: number; readonly inline?: boolean; readonly onOpen: () => void }): ReactNode {
  if (props.count <= 0) return null;
  return (
    <button
      type="button"
      className={props.inline === true ? "t-cart-badge t-cart-badge--inline" : "t-cart-badge"}
      aria-label={`Cart, ${props.count} ${props.count === 1 ? "item" : "items"}`}
      onClick={props.onOpen}
    >
      <Icon name="cart" /> {props.count}
    </button>
  );
}

/**
 * B282 — the announcement a link that LEAVES owes its reader. Six anchors
 * in the fleet opened a new tab and none said so; six tests had already
 * pinned `target="_blank"` by name, so the fleet walled that these links
 * do leave and never that the operator is told. Split in two on purpose:
 * `NewTabHint` is the WORDING (one place, so no site can drift into its
 * own phrasing) and `NewTabLink` is the anchor that carries it. The shell's
 * nav link renders the hint directly, because a nav link is not an
 * ordinary anchor — same words, one implementation.
 *
 * The mark is decorative (`aria-hidden`) and the words are visually
 * hidden: a sighted reader gets the affordance, assistive tech gets the
 * sentence, and neither gets it twice. WCAG 2.2 SC 3.2.5.
 */
export function NewTabHint(): ReactNode {
  return (
    <>
      <span className="t-newtab-mark" aria-hidden="true">
        <Icon name="arrow-out" size={12} />
      </span>
      <span className="t-visually-hidden"> (opens in a new tab)</span>
    </>
  );
}

/** A link that leaves the app: opens away, withholds the referrer, and
 * says so. The caller keeps its own label and class — this is the same
 * link it always was, with the warning it always owed. */
export function NewTabLink(props: { readonly href: string; readonly className?: string; readonly children: ReactNode }): ReactNode {
  return (
    <a className={props.className} href={props.href} target="_blank" rel="noreferrer">
      {props.children}
      <NewTabHint />
    </a>
  );
}

/**
 * B285 — the LIST, which this package never had. Pathik walked Selling →
 * Schedules & slots and found the Departures list rendering as raw
 * `<ul>/<li>`: browser-default discs and margins, and its text colliding
 * (`— Closedthis departure has already ended…`). Both halves are one
 * omission. There was nothing here to reach for, so 26 list containers
 * across four apps reached for the browser's own, and the drift wall
 * could not see them because its regex named controls and not lists.
 *
 * The collision is the half worth designing against rather than merely
 * fixing. The row was separating its facts with LEADING SPACES inside
 * string literals — so whether two facts read as two facts depended on
 * an invisible character in a template, which no reviewer can see and no
 * wall could check. Here each part is its own element and the gap lives
 * in the stylesheet, so the seam is structural.
 *
 * `heading` / `status` / `note` / `actions` are the shape an operator
 * row actually has (what this is, where it stands, why a door is shut,
 * the doors) — but they are all optional, and a Row given nothing but
 * children is still a Row. A primitive that forced its shape would just
 * send the next screen back to the browser's list.
 */
/* ---------------------------------------------------- TotalRow (B355 iv) */

/**
 * B355 leg (iv) — the exemplar's totals row: the one figure in a column
 * of figures that the operator must not misread.
 *
 * `pos-sale.png` separates the sale's lines from its TOTAL with a rule
 * visibly heavier than the hairlines between the lines. The till today
 * renders `Total: <strong>$34.00</strong>` — a sentence, weighted the
 * same as everything above it, which is the B287 shape one more time:
 * the number is correct and the screen does not say it is the important
 * one.
 *
 * It lives here rather than in `apps/pos` because it is owed twice
 * already — the pages README lists a "reports weekly totals row" under
 * Money against the same exemplar set. Spelled once in the till it would
 * be invisible to Money (the UI law, RULING 2026-07-26), and the second
 * writing is where the two drift apart.
 *
 * The amount POINTS AT the label (`aria-labelledby`) rather than
 * repeating it. A screen-reader operator hearing "$34.00" with no noun
 * has been told a number, not a total; and a visually-hidden second
 * "Total" would be B285's twin-name defect, which this package has
 * already cured once.
 */
export function TotalRow(props: {
  /** The noun — "Total", "This week". Rendered, and pointed at by the figure. */
  readonly label: string;
  /** The figure, already formatted by the caller's own money derivation. */
  readonly amount: ReactNode;
  /** One quiet line under the figure — what the total does or does not include. */
  readonly note?: string;
}): ReactNode {
  const uid = useId();
  return (
    <div className="t-total-row">
      <span className="t-total-row-label" id={`${uid}-label`}>
        {props.label}
      </span>
      <span className="t-total-row-value" aria-labelledby={`${uid}-label`}>
        {props.amount}
      </span>
      {props.note === undefined ? null : <span className="t-total-row-note">{props.note}</span>}
    </div>
  );
}

export function RowList(props: {
  /** names the list for AT where no visible heading already does */
  readonly label?: string;
  /** a numbered sequence — a different fact from a set */
  readonly ordered?: boolean;
  /** a prose list (a Banner's findings, a disclosure) keeps its disc, painted from tokens */
  readonly bullets?: boolean;
  readonly children: ReactNode;
}): ReactNode {
  const className = `t-rows${props.bullets === true ? " t-rows--bullets" : ""}`;
  if (props.ordered === true) {
    return (
      <ol className={className} aria-label={props.label}>
        {props.children}
      </ol>
    );
  }
  return (
    <ul className={className} aria-label={props.label}>
      {props.children}
    </ul>
  );
}

/**
 * B390 / PAGE REVIEW #4(a) — the frame's HEADERLESS RECORD LIST (Pathik:
 * RECENT ORDERS was "way off" — rows jammed right at ~32px pitch with no
 * column tracks). A record list is rows of mixed-shape FACTS on declared
 * grid tracks at pitch 56, labeled by nothing: it is legible because each
 * fact is shaped differently (a code chip, a product, a time, a toned
 * word, a right-aligned figure), not because a header captions it.
 * `DataTable` keeps its headers for operator LISTS; this is the frame's
 * record card. Money → Orders and Bookings draw the same primitive with
 * their own tracks — which is why the tracks are the CONSUMER's, declared
 * inline as a custom property the sheet reads.
 */
export function RecordList(props: {
  /** names the list for AT — the card label does not reach the list */
  readonly label: string;
  /** the consumer's grid tracks, e.g. "104px 1fr 200px 240px 80px" */
  readonly tracks: string;
  /**
   * B392 — whether this list's tracks RESERVE the code column. Default
   * true, which is every list that existed when this grew.
   *
   * There are two different absences here, one column apart, and only
   * the first is the row's. An absent `code` on a row is the
   * EMPTY-FACED chip: the list reserved the column, this record has no
   * human code, and the wash holding the column open is the honest
   * absence (review #4(a) — "not a '—' pill"). A list whose FRAME has
   * no code column at all is the other one, and it belongs here,
   * because the list is what declares the tracks — a row cannot know.
   * `redline-pos-sale.md` line 42 draws the till's sale line as "name
   * (196 wide) · stepper 108x43 · amount right-aligned"; a chip there
   * would be a column the frame does not have, holding itself open.
   */
  readonly code?: boolean;
  readonly children: ReactNode;
}): ReactNode {
  return (
    <ul className="t-records" aria-label={props.label} style={{ "--t-record-tracks": props.tracks } as CSSProperties}>
      <RecordCodeColumn.Provider value={props.code ?? true}>{props.children}</RecordCodeColumn.Provider>
    </ul>
  );
}

/* The code column is the LIST's fact, so the rows read it rather than
   repeating it — a row prop would be a second copy of `tracks` to keep in
   sync, which is exactly the drift this primitive exists to end. */
const RecordCodeColumn = createContext(true);

/**
 * One record — chip · primary · secondary · status · amount, each an
 * optional slot so a consumer with fewer facts declares fewer tracks.
 * The CHIP is always drawn when the list's tracks reserve it: an absent
 * code is an EMPTY-FACED chip (review #4(a) — "not a '—' pill"), because
 * the wash holding the column open IS the honest absence; the system id
 * keeps riding the row's title (B122), never its face.
 */
export function RecordRow(props: {
  /**
   * B355 — the record's STATE DOT (the fleet exemplar's opening mark).
   * Decorative by contract: the same fact must always ride the row's
   * words, so the dot is aria-hidden and carries no text — color is
   * never the only channel. Absent means no cell is drawn; a list that
   * wants the dot reserves its track, exactly like the chip.
   */
  readonly dot?: "good" | "warn" | "alert" | "neutral";
  /** the human code in the chip; absent = the empty-faced chip */
  readonly code?: string;
  readonly primary: ReactNode;
  /**
   * B355 — the record's SECOND LINE, stacked UNDER the primary inside
   * the same cell ("in service · last seen 2 min ago"). Not `secondary`
   * (that is its own track, one column over) and not `status` (a toned
   * word, not a sentence). When absent the DOM is unchanged — a row
   * with no second line must not grow a wrapper.
   */
  readonly detail?: ReactNode;
  /**
   * B518 — the primary's DIMMER TRAILING CLAUSE, on the same line: the
   * fleet frame's "Ticket scanner — Harbor Pier — gate 2", the clause in
   * the card-head ink. The component draws the spaced dash so every
   * caller joins the two voices the same way. Absent = DOM unchanged.
   */
  readonly aside?: ReactNode;
  /**
   * B518 — the record no longer acts (a revoked device): its primary dims
   * to the aside's ink. The words must still say why (the detail line);
   * the dimming is emphasis, never the only channel.
   */
  readonly retired?: boolean;
  readonly secondary?: ReactNode;
  /** a toned word — pass the existing Badge; the list scopes its tracking */
  readonly status?: ReactNode;
  /** the caller's own money words — this slot does not format */
  readonly amount?: ReactNode;
  /**
   * B392 — the record's DOORS. The till's sale line needs a Remove and
   * none of the slots above is one: `status` is where the thing stands
   * in the operator's words, and a door is not a status; a button in
   * `amount` or `secondary` is the same category error one field over.
   * `Row` already carries this slot and this ordering (facts -> figure
   * -> doors); the record now agrees with it.
   */
  readonly actions?: ReactNode;
  /** B122 — the system id rides the row's title, never its face */
  readonly systemId?: string;
}): ReactNode {
  const code = useContext(RecordCodeColumn);
  const primary =
    props.aside === undefined ? (
      props.primary
    ) : (
      <>
        {props.primary}
        <span className="t-record-aside"> — {props.aside}</span>
      </>
    );
  return (
    /* B403 #45 (2026-09-02, Pathik): a machine id NEVER rides a hover
       face — it lives in data-system-id, not title. */
    <li className={props.retired === true ? "t-record t-record--retired" : "t-record"} data-system-id={props.systemId}>
      {props.dot === undefined ? null : <span className={`t-record-dot t-record-dot--${props.dot}`} aria-hidden="true"></span>}
      {code ? (
        props.code ? (
          <span className="t-record-chip">{props.code}</span>
        ) : (
          /* B403 measure finding #3 (2026-08-25): an EMPTY chip reads as
             broken — the absence says the dash on the washless chip. */
          <span className="t-record-chip t-record-chip--absent">—</span>
        )
      ) : null}
      {props.detail === undefined ? (
        <span className="t-record-primary">{primary}</span>
      ) : (
        <span className="t-record-main">
          <span className="t-record-primary">{primary}</span>
          <span className="t-record-detail">{props.detail}</span>
        </span>
      )}
      {props.secondary === undefined ? null : <span className="t-record-secondary">{props.secondary}</span>}
      {props.status === undefined ? null : <span className="t-record-status">{props.status}</span>}
      {props.amount === undefined ? null : <span className="t-record-amount">{props.amount}</span>}
      {props.actions === undefined ? null : <span className="t-record-actions">{props.actions}</span>}
    </li>
  );
}

/* --------------------------------------------------------------- RailRow */

/**
 * B542 (332:7 / 332:53) — one row of a 420px SIDE RAIL: two stacked lines
 * left, up to two stacked right. The six-slot RecordRow wraps inside a
 * rail's width (the defect Pathik screenshotted on the operator Alerts
 * page); this is the frame's own narrow anatomy. The left stack is a
 * lead line and a muted sentence CLAMPED at two lines; the right stack
 * is a figure (or, `tailQuiet`, the frame's small time register) over a
 * chip. The 7px dot is decorative by the same contract as the record's:
 * the words always carry the fact, color is never the only channel.
 * Line-1 lockups compose the package's own registers:
 * `.t-railrow-actor` (the Rajdhani caps actor) and `.t-railrow-word`
 * (the lowercase state word beside it).
 */
export function RailRow(props: {
  readonly dot?: "good" | "warn" | "alert" | "neutral";
  /** left line 1 — the row's name, or an actor lockup */
  readonly lead: ReactNode;
  /** left line 2 — one muted sentence, two lines max, ellipsis */
  readonly sub?: ReactNode;
  /** right line 1 — the caller's own words (this slot does not format) */
  readonly tail?: ReactNode;
  /** the tail in the frame's small quiet register (an age, not a figure) */
  readonly tailQuiet?: boolean;
  /** right line 2 — the status chip (pass the existing Badge) */
  readonly tailSub?: ReactNode;
  /** B122 — the system id rides data-system-id, never the row's face */
  readonly systemId?: string;
}): ReactNode {
  return (
    <li className="t-railrow" data-system-id={props.systemId}>
      {props.dot === undefined ? null : <span className={`t-railrow-dot t-railrow-dot--${props.dot}`} aria-hidden="true"></span>}
      <span className="t-railrow-main">
        <span className="t-railrow-lead">{props.lead}</span>
        {props.sub === undefined ? null : <span className="t-railrow-sub">{props.sub}</span>}
      </span>
      {props.tail === undefined && props.tailSub === undefined ? null : (
        <span className="t-railrow-side">
          {props.tail === undefined ? null : (
            <span className={props.tailQuiet === true ? "t-railrow-tail t-railrow-tail--quiet" : "t-railrow-tail"}>{props.tail}</span>
          )}
          {props.tailSub === undefined ? null : <span className="t-railrow-tailsub">{props.tailSub}</span>}
        </span>
      )}
    </li>
  );
}

/**
 * B564 (Figma 328-2, ACTIVITY) — a dated trail of what happened to one
 * thing: when · dot · what, oldest first, hairline-separated. Not a
 * RecordList (whose dot leads and whose tracks are the consumer's) and not
 * a RailList (two stacked lines a side): the frame's trail is one line per
 * event with the time in its own quiet column. The dot is decorative by the
 * same contract as every other dot — the words always carry the fact.
 */
export function Timeline(props: { readonly label: string; readonly children: ReactNode }): ReactNode {
  return (
    <ol className="t-timeline" aria-label={props.label}>
      {props.children}
    </ol>
  );
}

export function TimelineEntry(props: {
  /** the caller's own time words — this slot does not format */
  readonly when: string;
  readonly dot?: "good" | "warn" | "alert" | "neutral";
  readonly children: ReactNode;
}): ReactNode {
  return (
    <li className="t-timeline-entry">
      <span className="t-timeline-when">{props.when}</span>
      <span className={`t-timeline-dot t-timeline-dot--${props.dot ?? "neutral"}`} aria-hidden="true"></span>
      <span className="t-timeline-what">{props.children}</span>
    </li>
  );
}

/**
 * B565 (Figma 331-2's head) — a few counts on the page title's own line:
 * figure then caps label, side by side ("3 OPEN · 1 CRITICAL"). Not a KPI
 * card (a card is a fact with a visual and a footnote); this is a glance
 * strip that rides `PageTitle`'s action slot. A tone paints the FIGURE
 * only — the label always says what the number is.
 */
export function FigureStrip(props: {
  readonly label: string;
  readonly children: ReactNode;
  /** B525 (c) — the strip's name in a redline sidecar (530:2's KPI strip). Omitted = no attribute. */
  readonly redline?: string;
}): ReactNode {
  return (
    <ul className="t-figstrip" aria-label={props.label} {...(props.redline !== undefined ? { "data-redline": props.redline } : {})}>
      {props.children}
    </ul>
  );
}

export function FigureStripItem(props: {
  readonly figure: ReactNode;
  readonly label: string;
  readonly tone?: "good" | "warn" | "alert";
}): ReactNode {
  return (
    <li className={props.tone === undefined ? "t-figstrip-item" : `t-figstrip-item t-figstrip-item--${props.tone}`}>
      <span className="t-figstrip-figure">{props.figure}</span>
      <span className="t-figstrip-label">{props.label}</span>
    </li>
  );
}

/** The rail rows' list — hairline-separated, no grid tracks to declare. */
export function RailList(props: { readonly label: string; readonly children: ReactNode }): ReactNode {
  return (
    <ul className="t-rail" aria-label={props.label}>
      {props.children}
    </ul>
  );
}

export function Row(props: {
  readonly heading?: ReactNode;
  /** where the thing stands, in the operator's words — never a wire enum */
  readonly status?: ReactNode;
  /** why a door is shut, or what the row wants read; stated once */
  readonly note?: ReactNode;
  /** the row's doors */
  readonly actions?: ReactNode;
  /**
   * B355 — the row's FIGURE, pushed to the end and set in tabular
   * numerals so a column of them lines up under itself. It does not
   * format: the caller's own money derivation owns the words, exactly
   * as `TotalRow` leaves it. A figure is not a door, which is why it
   * does not ride `actions`, and not a reason, which is why it does not
   * ride `note` (that slot takes the whole next line, by design).
   */
  readonly amount?: ReactNode;
  /** B122 — the system id rides the row's title, never its face */
  readonly systemId?: string;
  readonly children?: ReactNode;
}): ReactNode {
  /* A row that uses the slots is laid out as a LINE (the gap is what
     keeps two facts from abutting); a row handed only children keeps
     ordinary block flow, because its content may be prose or a
     paragraph and a flex container would chop that into pieces. Both
     lose the browser's disc and margin, which is the defect. */
  const parts =
    props.heading !== undefined ||
    props.status !== undefined ||
    props.note !== undefined ||
    props.amount !== undefined ||
    props.actions !== undefined;
  return (
    <li className={parts ? "t-row t-row--parts" : "t-row"} data-system-id={props.systemId}>
      {props.heading === undefined ? null : <span className="t-row-heading">{props.heading}</span>}
      {props.status === undefined ? null : <span className="t-row-status">{props.status}</span>}
      {props.note === undefined ? null : <span className="t-row-note">{props.note}</span>}
      {props.children}
      {props.amount === undefined ? null : <span className="t-row-amount">{props.amount}</span>}
      {props.actions === undefined ? null : <span className="t-row-actions">{props.actions}</span>}
    </li>
  );
}

/**
 * B285 — the fact list. Three screens hand-rolled `<dl><div><dt>…<dd>`
 * for their Technical-details drawers and the validator's manifest
 * summary; all three were browser-painted, and one of them carried an
 * attribute (`<dl title={slotId}>`) which is exactly why the wall tests
 * for a className and not for "bare".
 *
 * Each pair is wrapped so a wide screen can column term against value
 * without `dt`/`dd` losing their pairing — the grouping element is
 * permitted inside `<dl>` for this purpose.
 */
export function FactList(props: {
  readonly label?: string;
  /** B122 — a system id may ride the list's title without being read aloud */
  readonly systemId?: string;
  /**
   * B579 (324-2) — `row`: the facts stand side by side, each label over its
   * value (the demo builder's business card: NAME · KIND · TIME ZONE · BRAND
   * in one line), wrapping when the card narrows. Default: one pair a line.
   */
  readonly layout?: "row";
  readonly children: ReactNode;
}): ReactNode {
  return (
    <dl
      className={props.layout === "row" ? "t-facts t-facts--row" : "t-facts"}
      aria-label={props.label}
      data-system-id={props.systemId}
    >
      {props.children}
    </dl>
  );
}

export function Fact(props: { readonly label: ReactNode; readonly children: ReactNode }): ReactNode {
  return (
    <div className="t-fact">
      <dt className="t-fact-label">{props.label}</dt>
      <dd className="t-fact-value">{props.children}</dd>
    </div>
  );
}

export function EmptyState(props: { title: string; body?: string; action?: ReactNode }): ReactNode {
  return (
    <div className="t-empty">
      <h2 className="t-empty-title">{props.title}</h2>
      {props.body === undefined ? null : <p className="t-empty-body">{props.body}</p>}
      {props.action ?? null}
    </div>
  );
}

export function Skeleton(props: { lines?: number; columns?: readonly string[] }): ReactNode {
  const lines = props.lines ?? 3;
  const columns = props.columns;
  if (columns !== undefined && columns.length > 0) {
    /* B403 (80:2, face 1 of the four-states board) — the LOADING face keeps
       the table's shape: the heads stand as real words and every row holds
       its height and columns, so the page never jumps when the rows land. */
    return (
      <div className="t-skeleton t-skeleton--table" aria-hidden="true">
        <div className="t-skeleton-heads">
          {columns.map((c) => (
            <span key={c} className="t-skeleton-head">
              {c}
            </span>
          ))}
        </div>
        {Array.from({ length: lines }, (_, i) => (
          <div key={i} className="t-skeleton-row">
            {columns.map((c) => (
              <span key={c} className="t-skeleton-line" />
            ))}
          </div>
        ))}
      </div>
    );
  }
  return (
    <div className="t-skeleton" aria-hidden="true">
      {Array.from({ length: lines }, (_, i) => (
        <span key={i} className="t-skeleton-line" />
      ))}
    </div>
  );
}

export function Breadcrumb(props: {
  items: readonly { readonly label: string; readonly href?: string; readonly onSelect?: () => void }[];
}): ReactNode {
  const last = props.items.length - 1;
  return (
    <nav className="t-breadcrumb" aria-label="Breadcrumb">
      <ol>
        {props.items.map((item, i) => (
          <li key={`${item.label}-${i}`}>
            {i === last ? (
              <span aria-current="page">{item.label}</span>
            ) : item.href === undefined ? (
              <span>{item.label}</span>
            ) : (
              <a
                href={item.href}
                onClick={
                  item.onSelect === undefined
                    ? undefined
                    : (e) => {
                        // B123(f): SPA hook, same guard as the shell's NavLink —
                        // the anchor still carries href for every other gesture.
                        if (e.defaultPrevented || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return;
                        e.preventDefault();
                        item.onSelect?.();
                      }
                }
              >
                {item.label}
              </a>
            )}
          </li>
        ))}
      </ol>
    </nav>
  );
}

function cellClass(column: {
  readonly align?: "amount" | "num";
  readonly register?: "muted" | "dim" | "product" | "strong";
  readonly hideBelow?: "lg";
}): string | undefined {
  const classes = [
    ...(column.align === "amount" ? ["t-cell--amount"] : []),
    ...(column.align === "num" ? ["t-cell--num"] : []),
    ...(column.register !== undefined ? [`t-cell--${column.register}`] : []),
    ...(column.hideBelow !== undefined ? [`t-cell--hide-below-${column.hideBelow}`] : []),
  ];
  return classes.length === 0 ? undefined : classes.join(" ");
}

export function DataTable(props: {
  /**
   * B290: `header` is the column's name and is never blank. A column
   * whose name would be noise on screen — the one holding the row's
   * own verb — sets `headerHidden`, which announces the name without
   * drawing it. The two are not interchangeable: an empty `header` is
   * a column nobody can name, which is what this screen shipped.
   */
  columns: readonly {
    readonly key: string;
    readonly header: string;
    readonly headerHidden?: boolean;
    /** B549 — the column caption's sidecar hook (438:2 names each). */
    readonly headerRedline?: string;
    /**
     * B367 (§4: "all amounts right-aligned to the container edge") — an
     * amount column DECLARES itself and every cell in it renders
     * right-aligned tabular. Declared, not inferred from content, so a
     * column of mixed strings cannot half-align.
     */
    readonly align?: "amount" | "num";
    /**
     * B361 (DESIGN-V2 §11.4(2)) — where this column sits in the DROP
     * ORDER as the table narrows: 1 is the row's headline and never
     * drops, 2 is the fact that rides beside it and never drops, and the
     * rest drop from the highest number down. Declared per column rather
     * than inferred from position because the leftmost column is very
     * often NOT the one a reader would keep (a checkbox, an avatar), and
     * a wall that guessed would be wrong quietly.
     *
     * A table that declares none renders no `data-drop-order` and the
     * §11 wall says so — the attribute is the declaration, so a table
     * cannot claim an order it did not state.
     */
    readonly priority?: number;
    /**
     * B403 (36:2) — the column's TYPE REGISTER: the frame tells buyers
     * (muted Light 14), whens (faint Light 14) and products (Regular
     * 14.5) apart by FACE, not by header. Declared per column so a cell
     * cannot half-adopt it.
     */
    readonly register?: "muted" | "dim" | "product" | "strong";
    /**
     * B403 (56:2) — the tier that HIDES this column. The 1024 bookings
     * frame drops CHANNEL and says so in its teaching line; the drop is
     * declared here so head and cells vanish together (CSS, tokens.css)
     * while the fact stays in the static markup for the Tier-2 wall.
     * "lg" = hidden below 1024px.
     */
    readonly hideBelow?: "lg";
  }[];
  rows: readonly {
    readonly key: string;
    readonly cells: Readonly<Record<string, ReactNode>>;
    /**
     * B122 — the system id rides the row's TITLE, never its face. `Row`,
     * `RecordRow` and the facts list have carried it since B122; the
     * operator TABLE could not, so every consumer with a reference to
     * expose had to choose between rendering a slug in a cell and
     * losing it. A table row is the same kind of thing they are.
     */
    readonly systemId?: string;
    /**
     * B403 (36:2) — the SELECTED row's overlay: accent wash + stroke.
     * The caller states the fact ("this row's record is open"); the
     * sheet draws it.
     */
    readonly selected?: boolean;
    /** B403 (63:2) — the TOTALS row (WEEK): heavier registers, the net in the display face. */
    readonly total?: boolean;
    /**
     * B403 (36:2) — the whole row is a DOOR into its record ("Click a
     * row for refunds and resends"). A real button, stretched over the
     * row (the Tile pattern), whose accessible name the caller states —
     * never a click handler on the <tr>, which no keyboard can reach.
     */
    readonly open?: { readonly label: string; readonly onOpen: () => void };
  }[];
  /**
   * B355 (settings-audit-log.png) — the frame's operator table is ONE glass
   * panel: heads, rows and a FOOT row (count sentence left, LOAD MORE door
   * right) on the same surface. `panel` wraps the table in that surface;
   * `foot` renders inside it and only with it, because a foot outside a
   * panel is a paragraph, not a card fact.
   */
  readonly panel?: true;
  readonly foot?: ReactNode;
  /** B403 (63:2) — the panel's own HEAD row (caps title left, fact right), above the table. */
  readonly head?: ReactNode;
  /** B386 — the panel's name in a redline sidecar (only drawn with `panel`). */
  readonly redline?: string;
  /**
   * B403 (80:2, `four-states-board.png`) — R12, verbatim: "Every list wears
   * all four — the same card, four honest faces. Skeleton shaped like the
   * content · empty with the fixing action · error with the retry inside the
   * banner · full. Nothing bare, nothing spinning forever."
   *
   * It lives HERE, on the one component every operator list already renders,
   * rather than in each screen's controller, because a doctrine each caller
   * re-implements is a doctrine each caller can get wrong — and for months
   * this one existed only as a PNG, which cannot fail. Absent `state` is the
   * FULL face, so every existing caller keeps its behavior untouched.
   *
   * The three parts that are doctrine rather than decoration:
   *   - the COLUMNS survive all four faces, so nothing jumps when rows land;
   *   - the retry is rendered INSIDE the banner, not beside it — the sentence
   *     that says what went wrong and the control that fixes it are one thing;
   *   - `error` does NOT blank the table. A failed refresh is not an absence
   *     of data: the last good rows stay drawn underneath and the screen SAYS
   *     they are stale, which is the honest reading and the useful one.
   *
   * `empty` takes its own `action` rather than inheriting one: an empty state
   * with no way forward is the thing DESIGN.md bans, and the shape makes the
   * caller supply the move that changes it.
   */
  readonly state?:
    | { readonly kind: "loading"; readonly rows?: number }
    | {
        readonly kind: "empty";
        readonly title: string;
        readonly body?: string;
        readonly action?: ReactNode;
      }
    | {
        readonly kind: "error";
        readonly message: string;
        readonly retry: { readonly label: string; readonly onRetry: () => void };
        /** The sentence under stale rows; the default says the plain truth. */
        readonly staleNote?: string;
      };
}): ReactNode {
  // The declared order, lowest priority number first. Emitted as data so a
  // STATIC render carries it: this is what B361's Tier-2 wall reads, and
  // what a row-cards renderer at <800px consumes to pick its emph line.
  const prioritised = props.columns.filter((c) => c.priority !== undefined);
  const dropOrder =
    prioritised.length === props.columns.length && props.columns.length > 0
      ? [...prioritised].sort((a, b) => (a.priority ?? 0) - (b.priority ?? 0)).map((c) => c.key).join(",")
      : undefined;
  const state = props.state;
  // The heads are drawn by the table itself in every face, so they are
  // named once here for the faces that replace the BODY rather than fill it.
  const headings = props.columns.filter((c) => c.headerHidden !== true).map((c) => c.header);

  const table = (
    <table className="t-table" data-drop-order={dropOrder}>
      <thead>
        <tr>
          {props.columns.map((column) => (
            <th scope="col" key={column.key} className={cellClass(column)}>
              {column.headerHidden === true ? (
                <span className="t-visually-hidden">{column.header}</span>
              ) : column.headerRedline !== undefined ? (
                <span data-redline={column.headerRedline}>{column.header}</span>
              ) : (
                column.header
              )}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {props.rows.map((row) => (
          <tr
            key={row.key}
            data-system-id={row.systemId}
            className={
              [...(row.selected === true ? ["t-row--selected"] : []), ...(row.total === true ? ["t-row--total"] : [])].join(" ") ||
              undefined
            }
          >
            {props.columns.map((column, i) => (
              <td key={column.key} className={cellClass(column)}>
                {i === 0 && row.open !== undefined ? (
                  <button type="button" className="t-rowdoor" aria-label={row.open.label} onClick={row.open.onOpen} />
                ) : null}
                {row.cells[column.key] ?? null}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  );
  /* B403 (80:2) — the three faces that are not the plain table. Each keeps
     the card and the columns; only the body below the heads changes. */
  const faced =
    state === undefined ? (
      table
    ) : state.kind === "loading" ? (
      <div className="t-table-state t-table-state--loading">
        <Skeleton columns={headings} lines={state.rows ?? 3} />
      </div>
    ) : state.kind === "empty" ? (
      <div className="t-table-state t-table-state--empty">
        <table className="t-table" data-drop-order={dropOrder}>
          <thead>
            <tr>
              {props.columns.map((column) => (
                <th scope="col" key={column.key} className={cellClass(column)}>
                  {column.headerHidden === true ? (
                    <span className="t-visually-hidden">{column.header}</span>
                  ) : (
                    column.header
                  )}
                </th>
              ))}
            </tr>
          </thead>
        </table>
        <EmptyState
          title={state.title}
          {...(state.body !== undefined ? { body: state.body } : {})}
          {...(state.action !== undefined ? { action: state.action } : {})}
        />
      </div>
    ) : (
      <div className="t-table-state t-table-state--error">
        <table className="t-table" data-drop-order={dropOrder}>
          <thead>
            <tr>
              {props.columns.map((column) => (
                <th scope="col" key={column.key} className={cellClass(column)}>
                  {column.headerHidden === true ? (
                    <span className="t-visually-hidden">{column.header}</span>
                  ) : (
                    column.header
                  )}
                </th>
              ))}
            </tr>
          </thead>
        </table>
        <Banner tone="danger">
          <span className="t-banner-say">{state.message}</span>
          <Button variant="neutral" onClick={state.retry.onRetry}>
            {state.retry.label}
          </Button>
        </Banner>
        {props.rows.length === 0 ? null : (
          /* The retry replays the same read; the last good rows stay visible
             underneath, and the note keeps the screen from passing stale
             rows off as fresh ones. */
          <div className="t-table-stale">
            <p className="t-table-stale-note">
              {state.staleNote ?? "Showing the rows we last loaded — they may have changed."}
            </p>
            {table}
          </div>
        )}
      </div>
    );

  if (props.panel !== true) return faced;
  return (
    <div className="t-tablepanel" {...(props.redline !== undefined ? { "data-redline": props.redline } : {})}>
      {props.head !== undefined ? <div className="t-tablepanel-head">{props.head}</div> : null}
      {faced}
      {props.foot !== undefined ? <div className="t-tablepanel-foot">{props.foot}</div> : null}
    </div>
  );
}

export function Dialog(props: {
  id: string;
  title: string;
  open: boolean;
  onClose: () => void;
  /** B386 — the dialog's name in a redline sidecar (GlassCard's contract). */
  redline?: string;
  /**
   * B587 — an editor's doors (Cancel · Done) in the dialog's own foot, after
   * the fields, so they stay where the eye finishes rather than wherever the
   * form happened to end.
   */
  actions?: ReactNode;
  /** B587 — room for a form with rows of fields (a product's ticket types); the body scrolls. */
  wide?: boolean;
  children: ReactNode;
}): ReactNode {
  if (!props.open) return null;
  return (
    <div className="t-dialog-overlay">
      <div
        id={props.id}
        className={props.wide === true ? "t-dialog t-dialog--wide" : "t-dialog"}
        role="dialog"
        aria-modal="true"
        aria-labelledby={`${props.id}-title`}
        {...(props.redline !== undefined ? { "data-redline": props.redline } : {})}
      >
        <button type="button" className="t-dialog-close" aria-label="Close" onClick={props.onClose}>
          ×
        </button>
        <h2 className="t-dialog-title" id={`${props.id}-title`}>
          {props.title}
        </h2>
        {props.children}
        {props.actions === undefined ? null : <div className="t-dialog-actions">{props.actions}</div>}
      </div>
    </div>
  );
}

/* ---------------------------------------------------------------- Nav */

export function Nav(props: {
  label?: string;
  brand?: ReactNode;
  items: readonly { readonly label: string; readonly href: string; readonly current?: boolean }[];
  actions?: ReactNode;
  /** B72(a): lay the same landmark out as a sidebar column. */
  vertical?: boolean;
}): ReactNode {
  return (
    <nav
      className={props.vertical === true ? "t-nav t-nav--vertical" : "t-nav"}
      aria-label={props.label ?? "Primary"}
    >
      {props.brand === undefined ? null : <span className="t-nav-brand">{props.brand}</span>}
      <ul className="t-nav-items">
        {props.items.map((item) => (
          <li key={item.href}>
            <a className="t-nav-link" href={item.href} aria-current={item.current === true ? "page" : undefined}>
              {item.label}
            </a>
          </li>
        ))}
      </ul>
      {props.actions === undefined ? null : <div className="t-nav-actions">{props.actions}</div>}
    </nav>
  );
}

/* ----------------------------------------------------- DateTimePicker */

/**
 * B150 — the skin. DESIGN.md has banned browser-default control faces
 * since B61, but `<input type="date">` renders the vendor's own chrome
 * (`mm/dd/yyyy`, the vendor calendar button, `--:-- --` for an empty
 * time) no matter what border we put around it. We keep the NATIVE input
 * — it is what gives us the mobile date wheel, keyboard entry and the
 * platform a11y contract, and a div pretending to be a date field would
 * lose all three — and cover its chrome instead:
 *
 *   - `data-empty` tells the sheet when there is no value, which is the
 *     only moment the vendor placeholder is visible;
 *   - our own operator-word placeholder paints over it, and yields the
 *     instant the control takes focus (the native editor must be able to
 *     do its job);
 *   - our calendar/clock glyph is decorative, and the vendor indicator
 *     stays at opacity 0 over the GLYPH'S LANE as the click target, so
 *     clicking the glyph opens the real picker while clicks in the text
 *     still focus a segment for typing (B421 #44(c): stretched over the
 *     whole field it swallowed every click and the field could not be
 *     typed into by mouse).
 *
 * Everything above is CSS in tokens.css; this component only renders the
 * hooks it needs. Both parts of the group share this one shape, so the
 * date and the time can never drift apart again.
 */
/** B403 #18 — "Fri 15 Aug · 00:00" from a datetime-local value; pure calendar words. */
function momentWords(value: string): string | undefined {
  const m = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/.exec(value);
  if (m === null) return undefined;
  const d = new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3])));
  const weekday = d.toLocaleDateString("en-GB", { weekday: "short", timeZone: "UTC" });
  const month = d.toLocaleDateString("en-GB", { month: "short", timeZone: "UTC" });
  return `${weekday} ${Number(m[3])} ${month} \u00b7 ${m[4]}:${m[5]}`;
}

function SkinnedDateTimeInput(props: {
  id: string;
  type: "date" | "time" | "datetime-local";
  value: string;
  placeholder: string;
  onValueChange: (value: string) => void;
  invalid: "true" | undefined;
  describedBy: string | undefined;
  /** B149: a dateOnly group's lone input is named by the legend — no sublabel to attach to. */
  labelledBy: string | undefined;
}): ReactNode {
  const empty = props.value === "";
  /* B403 #18 — the combined moment wears the frames' WORD-FACE ("Fri 15
     Aug · 00:00") over the native control: the input stays real (focus
     reveals the native editor, exactly the placeholder's pattern), the
     words are what an operator reads at rest. Date/time-only inputs keep
     their native faces. */
  const wordFace = props.type === "datetime-local" && !empty ? momentWords(props.value) : undefined;
  return (
    <span className="t-datetime-control" data-empty={empty ? "true" : "false"} data-worded={wordFace !== undefined ? "true" : "false"}>
      <input
        className="t-input"
        id={props.id}
        type={props.type}
        value={props.value}
        onChange={(e) => props.onValueChange(e.currentTarget.value)}
        aria-invalid={props.invalid}
        aria-describedby={props.describedBy}
        aria-labelledby={props.labelledBy}
      />
      {empty ? <span className="t-datetime-placeholder">{props.placeholder}</span> : null}
      {wordFace !== undefined ? (
        <span className="t-datetime-wordface" aria-hidden="true">
          {wordFace}
        </span>
      ) : null}
      <svg
        className="t-datetime-glyph"
        aria-hidden="true"
        focusable="false"
        viewBox="0 0 16 16"
        width="16"
        height="16"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.4"
        strokeLinecap="round"
      >
        {props.type !== "time" ? (
          <>
            <rect x="2" y="3.2" width="12" height="11" rx="2" />
            <path d="M2 6.6h12M5.4 1.8v2.6M10.6 1.8v2.6" />
          </>
        ) : (
          <>
            <circle cx="8" cy="8" r="6.2" />
            <path d="M8 4.4V8l2.4 1.6" />
          </>
        )}
      </svg>
    </span>
  );
}

/**
 * B421 #44(c) — the combined moment's ONE edit as ONE change. The
 * combined branch used to split an edit into onDateChange + onTimeChange,
 * two parent updates in one breath; every caller's second handler closed
 * over the PREVIOUS render's other half, so the time write carried
 * date="" and clobbered the date write — measured live: filling WINDOW
 * START left the control empty ("the pickers don't work", Pathik's #44c
 * walk). A caller that offers onMomentChange receives both halves
 * atomically; the pair calls remain only as the legacy fallback.
 */
export function combinedMomentHandler(cbs: {
  readonly onMomentChange?: ((date: string, time: string) => void) | undefined;
  readonly onDateChange: (value: string) => void;
  readonly onTimeChange?: ((value: string) => void) | undefined;
}): (v: string) => void {
  return (v: string): void => {
    const [d, t] = v.split("T");
    const date = d ?? "";
    const time = t === undefined || t === "" ? "" : t.slice(0, 5);
    if (cbs.onMomentChange !== undefined) {
      cbs.onMomentChange(date, time);
      return;
    }
    cbs.onDateChange(date);
    cbs.onTimeChange?.(time);
  };
}

export function DateTimePicker(props: {
  id: string;
  label: string;
  date: string;
  /** Required unless dateOnly — a rendered time control always has a value. */
  time?: string;
  onDateChange: (value: string) => void;
  onTimeChange?: (value: string) => void;
  /** B421 #44(c): the combined branch's atomic edit — see combinedMomentHandler. */
  onMomentChange?: ((date: string, time: string) => void) | undefined;
  timeOptions?: readonly { readonly value: string; readonly label: string }[];
  /** B121: a date-range bound is a DATE — render no time part at all. */
  dateOnly?: boolean;
  /**
   * B193 (R1): the mirror — a recurrence's departure is a wall-clock TIME
   * with no day of its own, so render no date part. Same B149 rule as
   * dateOnly: one control, named by the legend, no sublabel to stutter.
   */
  timeOnly?: boolean;
  /** B117 field scale for the whole group (dates are usually "sm"). */
  width?: FieldWidth;
  hint?: string;
  error?: string;
  disabled?: boolean;
}): ReactNode {
  const invalid = props.error === undefined ? undefined : ("true" as const);
  const described = describedBy(props.id, props.hint, props.error);
  const widthClass = props.width === undefined ? "" : ` t-field--${props.width}`;
  /* B403 #13-r — a free MOMENT is ONE combined control (68:2 draws one
     select face, "Fri 15 Aug · 00:00"), not a day/time pair. #13 (a)'s
     CSS-joined pair survives ONLY where a fixed slot list makes the time
     a real <select> (timeOptions); dateOnly/timeOnly keep their single
     controls. The humane word-face rides #18's rider with the audit
     selects — the control shape is the frame's now. */
  if (props.dateOnly !== true && props.timeOnly !== true && props.timeOptions === undefined) {
    const at = props.date === "" ? "" : `${props.date}T${props.time === undefined || props.time === "" ? "00:00" : props.time}`;
    return (
      <fieldset className={`t-datetime t-datetime--one${widthClass}`} disabled={props.disabled === true}>
        <legend className="t-field-label" id={`${props.id}-label`}>
          {props.label}
        </legend>
        <span className="t-datetime-part">
          <SkinnedDateTimeInput
            id={`${props.id}-at`}
            type="datetime-local"
            value={at}
            placeholder="Pick a day and time"
            onValueChange={combinedMomentHandler(props)}
            invalid={invalid}
            describedBy={described}
            labelledBy={`${props.id}-label`}
          />
        </span>
        {props.hint !== undefined && (
          <span className="t-field-hint" id={`${props.id}-hint`}>
            {props.hint}
          </span>
        )}
        {props.error !== undefined && (
          <span className="t-field-error" id={`${props.id}-error`}>
            {props.error}
          </span>
        )}
      </fieldset>
    );
  }
  const joined = props.dateOnly !== true && props.timeOnly !== true ? " t-datetime--joined" : "";
  return (
    <fieldset className={`t-datetime${joined}${widthClass}`} disabled={props.disabled === true}>
      <legend className="t-field-label" id={`${props.id}-label`}>
        {props.label}
      </legend>
      {props.timeOnly === true ? null : (
      <span className="t-datetime-part">
        {/* B149 — one label per field: a dateOnly group has ONE control, so
            the group label IS the field label; a Date sublabel would be a
            second label for the same thing (the From/Date stutter). The
            lone input stays named via aria-labelledby on the legend. */}
        {props.dateOnly === true ? null : (
          <label className="t-field-sublabel" htmlFor={`${props.id}-date`}>
            Date
          </label>
        )}
        <SkinnedDateTimeInput
          id={`${props.id}-date`}
          type="date"
          value={props.date}
          placeholder="Pick a day"
          onValueChange={props.onDateChange}
          invalid={invalid}
          describedBy={described}
          labelledBy={props.dateOnly === true ? `${props.id}-label` : undefined}
        />
      </span>
      )}
      {props.dateOnly === true ? null : (
        <span className="t-datetime-part">
          {props.timeOnly === true ? null : (
            <label className="t-field-sublabel" htmlFor={`${props.id}-time`}>
              Time
            </label>
          )}
          {props.timeOptions === undefined ? (
            <SkinnedDateTimeInput
              id={`${props.id}-time`}
              type="time"
              value={props.time ?? ""}
              placeholder="Pick a time"
              onValueChange={(v) => props.onTimeChange?.(v)}
              invalid={invalid}
              describedBy={described}
              labelledBy={props.timeOnly === true ? `${props.id}-label` : undefined}
            />
          ) : (
            <select
              className="t-select"
              id={`${props.id}-time`}
              value={props.time ?? ""}
              onChange={(e) => props.onTimeChange?.(e.currentTarget.value)}
              aria-invalid={invalid}
              aria-describedby={described}
            >
              {props.timeOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          )}
        </span>
      )}
      {props.hint === undefined ? null : (
        <span className="t-field-hint" id={`${props.id}-hint`}>
          {props.hint}
        </span>
      )}
      {props.error === undefined ? null : (
        <span className="t-field-error" id={`${props.id}-error`}>
          {props.error}
        </span>
      )}
    </fieldset>
  );
}

/* ---------------------------------------------------------- ThemeToggle */

/**
 * B72(b) — the light/dark switcher every surface mounts. Uncontrolled
 * by default (wires itself through useTransactTheme: persisted choice,
 * prefers-color-scheme fallback, document-root repaint); pass
 * theme/onToggle to control it (gallery previews, tests).
 */
export function ThemeToggle(props: {
  theme?: "light" | "dark";
  onToggle?: () => void;
}): ReactNode {
  const [autoTheme, autoToggle] = useTransactTheme();
  const theme = props.theme ?? autoTheme;
  const dark = theme === "dark";
  return (
    <button
      type="button"
      className="t-btn t-btn--ghost t-theme-toggle"
      aria-pressed={dark}
      aria-label={dark ? "Switch to light theme" : "Switch to dark theme"}
      onClick={props.onToggle ?? autoToggle}
    >
      {/* B368: a small ICON in the header — the frame draws no word; the
          state is announced (aria-pressed + the label), never spelled. */}
      <span aria-hidden="true">{dark ? "\u263e" : "\u2600"}</span>
    </button>
  );
}

/* ------------------------------------------------------------- GlassCard */

/** The corner action on a GlassCard: a flat icon (§7), named for AT. */
export interface GlassCardAction {
  readonly icon: IconName;
  readonly label: string;
  readonly onClick?: () => void;
  /** Given an href the action renders as a link instead of a button. */
  readonly href?: string;
}

/**
 * B353(a) — THE v2 card (DESIGN-V2 §2/§4): glass fill + hairline +
 * backdrop blur ride the sheet's glass tokens; this component owns the
 * ANATOMY — 8px status dot + letterspaced label + corner icon action +
 * content + plain-language footnote slot. Sizes step the sheet's blur and
 * radius (small 24 / default 28 / dialog 32; radius 10, dialogs 14).
 *
 * Composition contract (§9, walled in glasscard.test.tsx): one big fact +
 * one visual + one footnote per card. A chart child already carries its
 * own footnote — a card around one leaves the `footnote` slot empty, or
 * the density wall says why not. Card (v1, above) survives only for
 * pre-v2 screens; B355 moves every card in every app onto this one.
 */
export function GlassCard(props: {
  /** The letterspaced §3 card label ("REFUND EXPOSURE" — the sheet sets the caps). */
  readonly label: string;
  /** B367 — an id for the label heading, so a wrapping landmark can aria-labelledby it (the manual's walkthroughs). */
  readonly labelId?: string;
  /** B355 review #2 — head-row extras before the corner action (the net-sales period chips ride the card HEAD, top-right). */
  readonly headExtra?: ReactNode;
  /** Review #3 — the big-card label register (R-SB 12 ls5, head-label ink); small cards keep the default 11/ls4 dim. */
  readonly labelSize?: "lg";
  /** B549 — the card label's sidecar hook (441:2 names every card caption). */
  readonly labelRedline?: string;
  /** The 8px status dot's verdict; omitted = no dot. `info` is the frame's blue — a fact's dot, not a verdict's (redline-pos-drawer.md COUNTED). */
  readonly dot?: "good" | "warn" | "alert" | "neutral" | "info";
  readonly action?: GlassCardAction;
  /** The operator sentence under the content (§9.7) — skip it when the child chart speaks its own. */
  readonly footnote?: ReactNode;
  readonly size?: "small" | "dialog";
  /** B355 — the frame's ALERT card (today.png SELLING BADLY, 113:2's fraud card): a danger-tinted glass, same anatomy. */
  readonly tone?: "alert";
  /**
   * B390 — the CANVAS card (RULING 2026-08-19, Pathik, on today.png's LIVE
   * ORDERS): the child fills the card edge to edge and the head + footnote
   * are OVERLAID on it, rather than the child sitting in a well between two
   * text rows. "Not a 380 well stacked under a head." The card states the
   * frame's box height itself, because a bled ground has no intrinsic size
   * to give one.
   */
  readonly bleed?: true;
  /**
   * B386 — the card's name in a redline (`data-redline`). A redline asks
   * about ONE box and every card on Today is a `.t-glasscard`, so without
   * an explicit name no selector can pick one out; `diffRedline` refuses a
   * selector that matches many rather than taking the first, which is
   * SELF-REVIEW #235's rule applied to the checker itself. Omitted = no
   * attribute at all, never an empty one.
   */
  readonly redline?: string;
  readonly children: ReactNode;
}): ReactNode {
  const rootClass = [
    "t-glasscard",
    ...(props.size !== undefined ? [`t-glasscard--${props.size}`] : []),
    ...(props.tone !== undefined ? [`t-glasscard--${props.tone}`] : []),
    ...(props.bleed === true ? ["t-glasscard--bleed"] : []),
  ].join(" ");
  const action =
    props.action === undefined ? null : props.action.href !== undefined ? (
      <a className="t-glasscard-action" href={props.action.href} aria-label={props.action.label}>
        <Icon name={props.action.icon} />
      </a>
    ) : (
      <button type="button" className="t-glasscard-action" onClick={props.action.onClick} aria-label={props.action.label}>
        <Icon name={props.action.icon} />
      </button>
    );
  return (
    <section className={rootClass} {...(props.redline !== undefined ? { "data-redline": props.redline } : {})}>
      <header className="t-glasscard-head">
        {props.dot !== undefined && <span className={`t-glasscard-dot t-glasscard-dot--${props.dot}`} aria-hidden="true" />}
        <h2
          className={props.labelSize === undefined ? "t-glasscard-label" : `t-glasscard-label t-glasscard-label--${props.labelSize}`}
          id={props.labelId}
          {...(props.labelRedline !== undefined ? { "data-redline": props.labelRedline } : {})}
        >
          {props.label}
        </h2>
        {props.headExtra}
        {action}
      </header>
      <div className="t-glasscard-body">{props.children}</div>
      {props.footnote !== undefined && <p className="t-footnote t-glasscard-foot">{props.footnote}</p>}
    </section>
  );
}

/* ------------------------------------------------------------ EntityCard */

/**
 * B355 — the v2 card for a THING WITH A NAME (a product, a booking, a
 * device): selling-catalog.png's product card. GlassCard's anatomy is a
 * METRIC's — caps label at the left, the number as the body. An entity
 * is called by its name, so the head inverts: the mixed-case title (§3
 * body-emphasis, 15px) sits left, and the §4 dot + letterspaced-caps
 * pair moves right as the entity's STATUS ("● ON SALE"). The dot faces
 * are GlassCard's own — one source for verdict colors — and the glass
 * recipe rides the same --transact-glass-* tokens at the small step.
 *
 * With onOpen the title is a real <button> (the keyboard door into the
 * entity, B347 press bar); without it, a plain heading.
 */
export function EntityCard(props: {
  /** The entity's NAME, mixed case — never shouted. */
  readonly title: string;
  /** Given, the title becomes the card's door. */
  readonly onOpen?: () => void;
  /** The §4 status pair at the head's right; omitted = no claim. */
  readonly status?: { readonly dot: "good" | "warn" | "alert" | "neutral"; readonly label: string };
  /** The §9.7 operator sentence under the body. */
  readonly footnote?: ReactNode;
  /** Names the entity for AT ("product Harbor Ferry Crossing"). */
  readonly ariaLabel?: string;
  readonly children: ReactNode;
}): ReactNode {
  const title =
    props.onOpen === undefined ? (
      <h3 className="t-entitycard-title">{props.title}</h3>
    ) : (
      <button type="button" className="t-entitycard-title t-entitycard-open" onClick={props.onOpen}>
        {props.title}
      </button>
    );
  return (
    <article className="t-entitycard" aria-label={props.ariaLabel}>
      <header className="t-entitycard-head">
        {title}
        {props.status === undefined ? null : (
          <span className="t-entitycard-status">
            <span className={`t-glasscard-dot t-glasscard-dot--${props.status.dot}`} aria-hidden="true" />
            <span className="t-entitycard-status-label">{props.status.label}</span>
          </span>
        )}
      </header>
      <div className="t-entitycard-body">{props.children}</div>
      {props.footnote === undefined ? null : <p className="t-footnote t-entitycard-foot">{props.footnote}</p>}
    </article>
  );
}

/* ------------------------------------------------------------ Accordion */

/**
 * B355 — the v2 DISCLOSURE ROW: selling-catalog.png's "Price lists" and
 * "Catalog history". One glass row: the mixed-case title left (§3
 * body-emphasis), a one-line STATE of what's inside in the teaching
 * register, a chevron at the right edge; open, a body grows below.
 *
 * Native <details>/<summary> — Enter/Space, focus and the AT semantics
 * come from the platform rather than a re-derived click handler. The
 * glass recipe rides the same --transact-glass-* tokens at the small
 * step, so a retune moves every card and row together.
 */
export function Accordion(props: {
  /** The row's subject, mixed case — never shouted. */
  readonly title: string;
  /** One line of STATE about what's inside; omitted = no claim made. */
  readonly summary?: string;
  /** The default face is collapsed — the exemplar draws both rows shut. */
  readonly defaultOpen?: boolean;
  /** Names the row for AT ("price lists"). */
  readonly ariaLabel?: string;
  readonly children: ReactNode;
}): ReactNode {
  return (
    <details className="t-accordion" open={props.defaultOpen === true ? true : undefined}>
      <summary className="t-accordion-summary" aria-label={props.ariaLabel}>
        <span className="t-accordion-title">{props.title}</span>
        {props.summary === undefined ? null : <span className="t-accordion-state">{props.summary}</span>}
        <span className="t-accordion-chevron" aria-hidden="true">
          <Icon name="chevron-down" />
        </span>
      </summary>
      <div className="t-accordion-body">{props.children}</div>
    </details>
  );
}



/* ---------------------------------------------------------- ProductCard */

/**
 * B397 — the BUYER's product card (redline-storefront-booking.md, 75:2):
 * the storefront booking grid's 384×206 glass card. Three zones: the
 * product's NAME with its one-line meta (route · kind — only facts a read
 * carries, absent otherwise); the price face (`from $12.00` when per-type
 * prices differ, the bare amount when one price, `by quote` when none)
 * beside an optional status chip (dot + one buyer sentence); and ONE
 * action. The ONE-primary rule (§4: one accent action per view) is the
 * CALLER's to hold — a card cannot see its siblings — and the storefront
 * suite walls it over the whole grid.
 *
 * The action's visible caption is a verb phrase ("Book a Day Pass"); when
 * the caller shortens the object (an em-dashed name would put a B172
 * dash-sentence on a button's face), `ariaLabel` carries the full product
 * name so assistive tech never gets the shortened half alone.
 */
export function ProductCard(props: {
  readonly title: string;
  /** One line of read-carried facts ("Harbor Pier → Island Landing · timed departure"). */
  readonly meta?: string;
  readonly price: { readonly kind: "from" | "exact"; readonly display: string } | { readonly kind: "quote" };
  /** The §4 status pair under the price; `good` is a live fact's dot, `neutral` a note's. */
  readonly chip?: { readonly dot: "good" | "neutral"; readonly label: string };
  readonly action: {
    readonly label: string;
    readonly onPress: () => void;
    readonly primary?: boolean;
    readonly ariaLabel?: string;
  };
  /** B386 — the card's name in a redline (`data-redline`); omitted = no attribute. */
  readonly redline?: string;
  /** B521 — the product's first photo, drawn over the card; absent = the card as 75:2 draws it. */
  readonly image?: { readonly url: string; readonly alt?: string };
  /** B521 — the description's first sentence, under the meta; absent = no line. */
  readonly summary?: string;
}): ReactNode {
  return (
    <article
      className={props.image !== undefined ? "t-productcard t-productcard--photo" : "t-productcard"}
      {...(props.redline !== undefined ? { "data-redline": props.redline } : {})}
    >
      {props.image !== undefined ? (
        <img className="t-productcard-photo" src={sizedPhotoUrl(props.image.url, 480)} alt={props.image.alt ?? ""} loading="lazy" />
      ) : null}
      <header className="t-productcard-head">
        <h3 className="t-productcard-title">{props.title}</h3>
        {props.meta !== undefined ? <p className="t-productcard-meta">{props.meta}</p> : null}
        {props.summary !== undefined ? <p className="t-productcard-summary">{props.summary}</p> : null}
      </header>
      <p className="t-productcard-price">
        <span className="t-productcard-amount">
          {props.price.kind === "quote" ? "by quote" : props.price.kind === "from" ? `from ${props.price.display}` : props.price.display}
        </span>
        {props.chip !== undefined ? (
          <span className="t-productcard-chip">
            <span className={`t-glasscard-dot t-glasscard-dot--${props.chip.dot}`} aria-hidden="true" />
            {props.chip.label}
          </span>
        ) : null}
      </p>
      <div className="t-productcard-act">
        <Button
          onClick={props.action.onPress}
          variant={props.action.primary === true ? "primary" : "neutral"}
          {...(props.action.ariaLabel !== undefined ? { ariaLabel: props.action.ariaLabel } : {})}
        >
          {props.action.label}
        </Button>
      </div>
    </article>
  );
}

/* ------------------------------------------------------------- CartChip */

/**
 * B397 — the shell head's cart chip (75:2): `CART · 0` at the header's
 * right. The count is CONTENT and zero is the empty FACE, not a hidden
 * control — CartBadge hides at zero because it floats over the buyer's
 * reading; a header slot holds its ground so the door to the cart is
 * always where the buyer left it. Painted 30px; pressable 44 via the
 * `.t-btn` reach pattern (B347 — the face and the bar are both true).
 */
export function CartChip(props: {
  readonly count: number;
  readonly onOpen: () => void;
  readonly redline?: string;
}): ReactNode {
  /* B287 — the face IS the accessible name: "Cart · 0" is drawn and
     announced as one thing; a wordier aria-label would put a count-shaped
     sentence in the announced name that the face never draws. */
  return (
    <button
      type="button"
      className="t-cartchip"
      onClick={props.onOpen}
      {...(props.redline !== undefined ? { "data-redline": props.redline } : {})}
    >
      Cart · {props.count}
    </button>
  );
}

/* ---------------------------------------------------------- GlassHeader */

/**
 * B397 — the sticky glass shell head (75:2: 1440×72, `#fff@.04` fill,
 * `#fff@.08` inside stroke, blur 24). Chrome only, AppCanvas's argument
 * one bar down: the FROST lives in the package because the app-drift
 * wall refuses frost in apps (B352) — apps compose brand, nav and
 * actions inside and never paint the glass themselves.
 */
export function GlassHeader(props: {
  readonly className?: string;
  readonly redline?: string;
  readonly children: ReactNode;
}): ReactNode {
  return (
    <header
      className={props.className === undefined ? "t-glasshead" : `t-glasshead ${props.className}`}
      {...(props.redline !== undefined ? { "data-redline": props.redline } : {})}
    >
      {props.children}
    </header>
  );
}

/* ---------------------------------------------------------- FlowStepper */

/**
 * B397 leg (2) — the buyer flow's pill stepper (72:2: TICKETS · CART ·
 * PAY · DONE). An ordered list, because progress has an order (the same
 * argument as the storefront's original Stepper); each pill is a dot +
 * letterspaced label. Three faces from the sheet: ACTIVE (white-14 fill),
 * COMPLETED (stroke pill, green dot — 73:2/74:2), FUTURE (stroke pill,
 * dim). Chrome only: the flow's truth lives in the reducer; a pill is
 * never a door backwards.
 */
/* ------------------------------------------------------- LifecycleRail */

export type LifecycleFace = "done" | "current" | "future";

/**
 * B549 (5) — 443:2's LIFECYCLE card: a record's whole life on one rail —
 * done steps green, the current step amber, what is still to come a hollow
 * disc with a muted caption. Unlike FlowStepper (a flow the operator walks)
 * nothing here is pressable: the rail is a FACT about the record, derived
 * by the caller. The face is never color alone — a done step says so in
 * words a screen reader hears, the current one carries aria-current, and
 * each step may carry a drawn note (a date, "due Thu 2 Oct").
 */
export function LifecycleRail(props: {
  readonly ariaLabel: string;
  readonly steps: readonly {
    readonly key: string;
    readonly label: string;
    readonly face: LifecycleFace;
    readonly note?: string | undefined;
    /** Sidecar hook on the step's caption. */
    readonly redline?: string | undefined;
  }[];
}): ReactNode {
  return (
    <ol className="t-lifecycle" aria-label={props.ariaLabel}>
      {props.steps.map((step) => (
        <li key={step.key} className={`t-lifecycle-step t-lifecycle-step--${step.face}`} {...(step.face === "current" ? { "aria-current": "step" } : {})}>
          <span className="t-lifecycle-dot" aria-hidden="true" />
          <span className="t-lifecycle-label" {...(step.redline !== undefined ? { "data-redline": step.redline } : {})}>
            {step.label}
          </span>
          {step.face === "done" ? <span className="t-visually-hidden"> — done</span> : null}
          {step.note !== undefined ? <span className="t-lifecycle-note">{step.note}</span> : null}
        </li>
      ))}
    </ol>
  );
}

export function FlowStepper(props: {
  readonly steps: readonly { readonly key: string; readonly label: string }[];
  readonly activeKey: string;
  readonly redline?: string;
}): ReactNode {
  const activeAt = props.steps.findIndex((s) => s.key === props.activeKey);
  return (
    <ol className="t-flowsteps" aria-label="Checkout progress" {...(props.redline !== undefined ? { "data-redline": props.redline } : {})}>
      {props.steps.map((step, i) => {
        const face = i === activeAt ? "active" : i < activeAt ? "done" : "future";
        return (
          <li
            key={step.key}
            className={`t-flowstep t-flowstep--${face}`}
            {...(face === "active" ? { "aria-current": "step" } : {})}
          >
            <span className="t-flowstep-dot" aria-hidden="true" />
            <span className="t-flowstep-label">{step.label}</span>
          </li>
        );
      })}
    </ol>
  );
}

/* ------------------------------------------------------------ StickyBar */

/**
 * B397 leg (2) — the sheet's sticky commit bar (72:2: fixed bottom,
 * frosted, top hairline; the summary sentence rides ABOVE the bar and
 * the ONE action inside it). Chrome only — the frost lives here because
 * the app-drift wall refuses frost in apps (B352). `summary` is the
 * caller's one sentence: the selection restated when the door is armed,
 * or what is still missing while it is shut (one statement, B285).
 */
export function StickyBar(props: {
  readonly summary?: ReactNode;
  /** Fixed-position chrome in an app; inline in the gallery (CartBadge's own precedent). */
  readonly inline?: boolean;
  /**
   * B521 (364-2) — on a wide face the bar stops floating and takes its seat
   * at the foot of the page's right column; under 1024 it floats as 72:2
   * draws it. The page's grid names the seat (`grid-area: door`).
   */
  readonly seatedWhenWide?: boolean;
  readonly redline?: string;
  readonly children: ReactNode;
}): ReactNode {
  /* review #1 (2c): the hook element IS the frame's 81 bar — children ride
     directly in it and the summary is lifted out of its box by the sheet
     (absolute, bottom 100%), so a measured `[data-redline]` answers with
     the BAR's height, which is the number the frame states. */
  return (
    <div
      className={`${props.inline === true ? "t-stickybar t-stickybar--inline" : "t-stickybar"}${props.seatedWhenWide === true ? " t-stickybar--seated" : ""}`}
      {...(props.redline !== undefined ? { "data-redline": props.redline } : {})}
    >
      {props.summary !== undefined ? <p className="t-stickybar-summary">{props.summary}</p> : null}
      {props.children}
    </div>
  );
}

/* ---------------------------------------------------- B523 sheet chrome */

/**
 * B523 — the booking sheet's numbered step trail (368-2: "1 Tickets ·
 * 2 Day & time · 3 Cart · 4 Pay" across the header). An ordered list,
 * FlowStepper's argument at trail scale — but where the pill stepper is
 * chrome only, a COMPLETED trail step is a DOOR BACK (the item's rule:
 * "the step trail is a nav for completed steps, never for future ones").
 * The active step is marked; future steps are inert facts.
 */
export function StepTrail(props: {
  readonly steps: readonly { readonly key: string; readonly label: string }[];
  readonly activeKey: string;
  /** Present = completed steps open; absent = read-only chrome. */
  readonly onVisit?: (key: string) => void;
  readonly redline?: string;
}): ReactNode {
  const activeAt = props.steps.findIndex((s) => s.key === props.activeKey);
  return (
    <ol className="t-steptrail" aria-label="Booking steps" {...(props.redline !== undefined ? { "data-redline": props.redline } : {})}>
      {props.steps.map((step, i) => {
        const face = i === activeAt ? "active" : i < activeAt ? "done" : "future";
        const words = (
          <>
            <span className="t-steptrail-num" aria-hidden="true">
              {i + 1}
            </span>
            <span className="t-steptrail-label">{step.label}</span>
          </>
        );
        return (
          <li key={step.key} className={`t-steptrail-step t-steptrail-step--${face}`} {...(face === "active" ? { "aria-current": "step" } : {})}>
            {face === "done" && props.onVisit !== undefined ? (
              <button type="button" className="t-steptrail-door" onClick={() => props.onVisit?.(step.key)}>
                {words}
              </button>
            ) : (
              words
            )}
          </li>
        );
      })}
    </ol>
  );
}

/**
 * B523 — the hold strip (368-2: "Seats held for 9:54" on an amber band
 * under the header). Speaks only over a live hold — the caller renders it
 * only while `holdExpiresAt` is in the future (§10: no hold, no claim).
 * Under two minutes it turns CLOSING (amber deepens); it never counts
 * below 0:00 — expiry is the flow machine's arrow, not this strip's.
 */
export function HoldStrip(props: {
  readonly msLeft: number;
  readonly redline?: string;
  /** B545 — the sidecar's hook on the strip's words. */
  readonly textRedline?: string;
}): ReactNode {
  const seconds = Math.max(0, Math.floor(props.msLeft / 1000));
  const words = `Seats held for ${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, "0")}`;
  return (
    <p
      className={props.msLeft < 120_000 ? "t-holdstrip t-holdstrip--closing" : "t-holdstrip"}
      role="status"
      {...(props.redline !== undefined ? { "data-redline": props.redline } : {})}
    >
      <span className="t-holdstrip-dot" aria-hidden="true" />
      <span className="t-holdstrip-words" {...(props.textRedline !== undefined ? { "data-redline": props.textRedline } : {})}>
        {words}
      </span>
    </p>
  );
}

/**
 * B523 — the sheet's header (368-2): back ‹ + the step's title left, the
 * trail centerd (the caller hides it under 900), the aside (language) and
 * the ✕ right. The ✕ posts `cancel` through the caller — the sheet is the
 * storefront's, so is the way out (B517's argument, now INSIDE the frame).
 */
export function SheetHead(props: {
  readonly title: string;
  readonly onBack?: (() => void) | undefined;
  readonly onClose?: (() => void) | undefined;
  readonly center?: ReactNode;
  readonly aside?: ReactNode;
  readonly redline?: string;
  /** B545 — the sidecar's hooks on the title and the ✕. */
  readonly titleRedline?: string;
  readonly closeRedline?: string;
  /** B549 — the ‹ back door's hook (the group sheets' sidecars name it). */
  readonly backRedline?: string;
}): ReactNode {
  return (
    <header className="t-sheethead" {...(props.redline !== undefined ? { "data-redline": props.redline } : {})}>
      {props.onBack !== undefined ? (
        <button
          type="button"
          className="t-sheethead-back"
          aria-label="Back"
          onClick={props.onBack}
          {...(props.backRedline !== undefined ? { "data-redline": props.backRedline } : {})}
        >
          {"\u2039"}
        </button>
      ) : null}
      <h2 className="t-sheethead-title" {...(props.titleRedline !== undefined ? { "data-redline": props.titleRedline } : {})}>
        {props.title}
      </h2>
      {props.center !== undefined ? <div className="t-sheethead-center">{props.center}</div> : null}
      {props.aside !== undefined ? <div className="t-sheethead-aside">{props.aside}</div> : null}
      {props.onClose !== undefined ? (
        <button
          type="button"
          className="t-sheethead-close"
          aria-label="Close"
          onClick={props.onClose}
          {...(props.closeRedline !== undefined ? { "data-redline": props.closeRedline } : {})}
        >
          <Icon name="close" size={16} />
        </button>
      ) : null}
    </header>
  );
}

/**
 * B523 — the sheet's sticky foot (368-2/370-2: the total large with its
 * sub-sentence left, the ONE door forward right). StickyBar's argument at
 * sheet scale; the frost stays in the package (B352's drift wall).
 */
export function SheetFoot(props: {
  readonly total?: ReactNode;
  readonly sub?: ReactNode;
  /** Fixed to the viewport bottom in an app; static in the gallery. */
  readonly pinned?: boolean;
  readonly children: ReactNode;
  readonly redline?: string;
  /** B545 — the sidecar's hooks on the total and its sub-sentence. */
  readonly totalRedline?: string;
  readonly subRedline?: string;
}): ReactNode {
  return (
    <footer
      className={props.pinned === true ? "t-sheetfoot t-sheetfoot--pinned" : "t-sheetfoot"}
      {...(props.redline !== undefined ? { "data-redline": props.redline } : {})}
    >
      <div className="t-sheetfoot-facts">
        {props.total !== undefined ? (
          <span className="t-sheetfoot-amount" {...(props.totalRedline !== undefined ? { "data-redline": props.totalRedline } : {})}>
            {props.total}
          </span>
        ) : null}
        {props.sub !== undefined ? (
          <p className="t-sheetfoot-sub" {...(props.subRedline !== undefined ? { "data-redline": props.subRedline } : {})}>
            {props.sub}
          </p>
        ) : null}
      </div>
      <div className="t-sheetfoot-door">{props.children}</div>
    </footer>
  );
}

/* --------------------------------------------------------- CartLineCard */

/**
 * B545 (370-2 / 431-344) — ONE cart line on the booking sheet: the product
 * photo (180×124, r10 at the card's 16px inset), the name, the when-line,
 * `2 × Adult` with the line total right-aligned, and Edit / Remove as the
 * tracked caps text links. No stepper — the frame lists it as what a build
 * must NOT draw on a line (the count changes through Edit). `redline` is
 * the base hook; the parts answer `<base>-photo|-name|-when|-qty|-total|
 * -edit|-remove`, the names the sidecar measures.
 */
export function CartLineCard(props: {
  readonly name: string;
  readonly when?: string | undefined;
  /** "2 × Adult" — the count and the class, in the buyer's words. */
  readonly qty: string;
  readonly total?: string | undefined;
  readonly photo?: { readonly url: string; readonly alt?: string | undefined } | undefined;
  readonly onEdit?: (() => void) | undefined;
  readonly onRemove?: (() => void) | undefined;
  /** Spoken names for the doors — the line's own sentence, so two lines' doors differ. */
  readonly editLabel?: string;
  readonly removeLabel?: string;
  /** The Remove act in flight (B338 — the door wears it, nothing else). */
  readonly removeBusy?: boolean;
  readonly redline?: string;
  /** Hints at the line (B31g) — the caller's Banners. */
  readonly children?: ReactNode;
}): ReactNode {
  const hook = (part: string) => (props.redline !== undefined ? { "data-redline": `${props.redline}${part}` } : {});
  return (
    <article className="t-cartline" {...hook("")}>
      {props.photo !== undefined ? (
        <img className="t-cartline-photo" src={sizedPhotoUrl(props.photo.url, 480)} alt={props.photo.alt ?? ""} loading="lazy" {...hook("-photo")} />
      ) : null}
      <div className="t-cartline-body">
        <h3 className="t-cartline-name" {...hook("-name")}>
          {props.name}
        </h3>
        {props.when !== undefined ? (
          <p className="t-cartline-when" {...hook("-when")}>
            {props.when}
          </p>
        ) : null}
        <p className="t-cartline-qtyrow">
          <span className="t-cartline-qty" {...hook("-qty")}>
            {props.qty}
          </span>
          {props.total !== undefined ? (
            <span className="t-cartline-total" {...hook("-total")}>
              {props.total}
            </span>
          ) : null}
        </p>
        {props.onEdit !== undefined || props.onRemove !== undefined ? (
          <div className="t-cartline-doors">
            {props.onEdit !== undefined ? (
              <Button
                variant="textlink-accent"
                onClick={props.onEdit}
                {...(props.editLabel !== undefined ? { ariaLabel: props.editLabel } : {})}
                {...(props.redline !== undefined ? { data: { redline: `${props.redline}-edit` } } : {})}
              >
                Edit
              </Button>
            ) : null}
            {props.onRemove !== undefined ? (
              <Button
                variant="textlink"
                onClick={props.onRemove}
                busy={props.removeBusy === true}
                {...(props.removeLabel !== undefined ? { ariaLabel: props.removeLabel } : {})}
                {...(props.redline !== undefined ? { data: { redline: `${props.redline}-remove` } } : {})}
              >
                Remove
              </Button>
            ) : null}
          </div>
        ) : null}
        {props.children}
      </div>
    </article>
  );
}

/* ------------------------------------------------------------ SheetCard */

/**
 * B511 — a hairline rule with its words in the middle ("or pay with card",
 * Figma 356-2): the seam between two ways to do the same thing. The label
 * wears the caps card-label register; the rules are the pill hairline, so
 * the divider reads as structure, never as a control.
 */
export function LabelledRule(props: {
  readonly label: string;
  readonly className?: string;
  /** B545 (1) — a sidecar hook (redline-embed-pay.json names the rule). */
  readonly redline?: string;
}): ReactNode {
  return (
    <div
      className={props.className === undefined ? "t-labelled-rule" : `t-labelled-rule ${props.className}`}
      role="separator"
      aria-label={props.label}
      {...(props.redline !== undefined ? { "data-redline": props.redline } : {})}
    >
      <span className="t-labelled-rule-text" aria-hidden="true">
        {props.label}
      </span>
    </div>
  );
}

/**
 * B397 leg (2) — the 390 sheet's ONE glass card (72:2: r14, blur 32,
 * pad 20, V gap 16 — the sheet steps the 1440 recipe up). Chrome only,
 * GlassHeader's argument at card scale: no head anatomy is forced (the
 * tickets card leads with the product's own name, not a caps label), the
 * frost and the recipe live in the package.
 */
export function SheetCard(props: {
  readonly className?: string;
  readonly redline?: string;
  readonly children: ReactNode;
}): ReactNode {
  return (
    <section
      className={props.className === undefined ? "t-sheetcard" : `t-sheetcard ${props.className}`}
      {...(props.redline !== undefined ? { "data-redline": props.redline } : {})}
    >
      {props.children}
    </section>
  );
}
